import type { PrereqData } from "./types";

/**
 * SINGLE SOURCE OF TRUTH for chapter prerequisites.
 * Supplied dataset — rendered exactly as provided.
 */
export const chapterPrerequisiteData: PrereqData = {
  jee: {
    physics: [
      // --- CLASS 11 PHYSICS ---
      {
        id: "phy-11-01",
        chapter: "Units, Dimensions & Errors",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic Algebra & Powers"],
        coreSkills: [
          "Significant figures rules and rounding off",
          "Dimensional formula writing for derived quantities ($M^a L^b T^c I^d$)",
          "Error propagation formulas for multiplication, division, and powers ($Z = A^b B^c$)",
          "Vernier Calliper & Screw Gauge least count formula with positive/negative zero error"
        ],
        commonMistakeWarning: "Relative error me absolute values add hoti hain, subtract nahi hoti ($\Delta Z/Z = b\Delta A/A + c\Delta B/B$)."
      },
      {
        id: "phy-11-02",
        chapter: "Mathematical Tools & Vectors",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Class 10 Trigonometry & Coordinate Geometry"],
        coreSkills: [
          "Differentiation rules: Power rule, Product rule, Quotient rule, Chain rule",
          "Integration: Standard polynomial, trigonometric, exponential, definite integral limits",
          "Vector resolution into orthogonal components ($A_x = A\cos\theta, A_y = A\sin\theta$)",
          "Dot product ($\vec{A}\cdot\vec{B}$) and Cross product ($\vec{A}\times\vec{B}$) determinant expansion"
        ],
        commonMistakeWarning: "Cross product anti-commutative hota hai ($\vec{A}\times\vec{B} = -\vec{B}\times\vec{A}$); direction Right-Hand Rule se nikalna zaroori hai."
      },
      {
        id: "phy-11-03",
        chapter: "Motion in a Straight Line (1D)",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Mathematical Tools & Vectors"],
        coreSkills: [
          "Slope of $s-t$ graph = velocity, slope of $v-t$ graph = acceleration",
          "Area under $v-t$ graph = displacement, area under $a-t$ graph = change in velocity",
          "Calculus for non-uniform acceleration ($v = ds/dt, a = dv/dt = v\,dv/ds$)",
          "Sign convention for motion under gravity ($g = -9.8\text{ m/s}^2$)"
        ],
        commonMistakeWarning: "Kinematic equations ($v=u+at, s=ut+\\frac{1}{2}at^2$) tabhi lagti hain jab acceleration constant ho; variable acceleration me integration lagana hoga."
      },
      {
        id: "phy-11-04",
        chapter: "Motion in a Plane (Projectiles & Relative Motion)",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Motion in a Straight Line", "Mathematical Tools & Vectors"],
        coreSkills: [
          "Decoupling 2D motion into independent X and Y 1D equations",
          "Equation of trajectory ($y = x\tan\theta - \\frac{gx^2}{2u^2\cos^2\theta}$)",
          "Projectile on inclined plane coordinate tilt",
          "Relative velocity in 2D ($\vec{v}_{AB} = \vec{v}_A - \vec{v}_B$), River-boat and Rain-man problems"
        ],
        commonMistakeWarning: "River crossing shortest path me boat ka resultant velocity river flow ke perpendicular hota hai, na ki boat ki heading."
      },
      {
        id: "phy-11-05",
        chapter: "Laws of Motion & Friction",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Motion in a Plane", "Mathematical Tools & Vectors"],
        coreSkills: [
          "Free Body Diagram (FBD) drawing with normal reaction, string tension, spring force",
          "String and wedge constraint equations ($T_1 x_1 + T_2 x_2 = 0$)",
          "Static friction ($f_s \le \mu_s N$) vs Kinetic friction ($f_k = \mu_k N$)",
          "Pseudo force ($\vec{F}_p = -m\vec{a}_{frame}$) in accelerating non-inertial frames"
        ],
        commonMistakeWarning: "Static friction hamesha $\mu_s N$ nahi hota; ye self-adjusting force hai jo applied force ke equal hoti hai up to limiting value."
      },
      {
        id: "phy-11-06",
        chapter: "Work, Energy & Power",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Laws of Motion & Friction"],
        coreSkills: [
          "Work done by variable force via integration ($W = \int \vec{F}\cdot d\vec{r}$)",
          "Work-Energy Theorem ($W_{all} = \Delta K$)",
          "Conservative force and Potential energy relation ($F = -\nabla U = -dU/dx$)",
          "Stable, unstable, and neutral equilibrium conditions ($d^2U/dx^2 > 0, < 0, = 0$)"
        ],
        commonMistakeWarning: "Mechanical energy conservation tabhi lagti hai jab non-conservative forces (jaise friction) ka work done zero ho."
      },
      {
        id: "phy-11-07",
        chapter: "Circular Motion",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Laws of Motion", "Work, Energy & Power"],
        coreSkills: [
          "Centripetal acceleration ($a_c = v^2/r = \omega^2 r$) vs Tangential acceleration ($a_t = dv/dt = r\alpha$)",
          "Banking of roads with and without friction",
          "Vertical circular motion critical velocity equations ($v_{bottom} = \sqrt{5gR}, v_{top} = \sqrt{gR}$)",
          "Centrifugal force in rotating reference frames"
        ],
        commonMistakeWarning: "Vertical circular motion me loop complete karne ki condition top point par velocity $> 0$ nahi, balki string tension $T \ge 0$ hona hai."
      },
      {
        id: "phy-11-08",
        chapter: "Center of Mass, Momentum & Collisions",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Work, Energy & Power", "Circular Motion"],
        coreSkills: [
          "Center of mass calculation for continuous bodies ($x_{cm} = \frac{\int x\,dm}{\int dm}$)",
          "Cut-out / Truncated body center of mass using negative mass concept",
          "Conservation of linear momentum when $\vec{F}_{ext} = 0$",
          "Coefficient of restitution ($e = \frac{v_2 - v_1}{u_1 - u_2}$) for 1D and oblique collisions"
        ],
        commonMistakeWarning: "Oblique collision me coefficient of restitution $e$ sirf line of impact (common normal) ke along apply hota hai, common tangent ke along velocity unchanged rehti hai."
      },
      {
        id: "phy-11-09",
        chapter: "Rotational Dynamics",
        classLevel: "11th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Center of Mass, Momentum & Collisions", "Circular Motion"],
        coreSkills: [
          "Moment of Inertia theorems: Parallel axis theorem ($I = I_{cm} + Md^2$) and Perpendicular axis theorem",
          "Torque vector equation ($\vec{\tau} = \vec{r}\times\vec{F} = I\vec{\alpha}$)",
          "Angular momentum conservation ($\vec{L} = I\vec{\omega} + \vec{r}_{cm}\times M\vec{v}_{cm}$)",
          "Pure rolling conditions on flat and inclined surfaces ($v_{cm} = R\omega, a_{cm} = R\alpha$)"
        ],
        commonMistakeWarning: "Perpendicular axis theorem sirf 2D planar laminar bodies ke liye valid hai, 3D sphere ya cylinder ke liye nahi."
      },
      {
        id: "phy-11-10",
        chapter: "Gravitation",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Circular Motion", "Work, Energy & Power"],
        coreSkills: [
          "Gravitational field and potential due to solid sphere and spherical shell",
          "Variation of $g$ with height ($g' = g(1 - 2h/R)$) and depth ($g' = g(1 - d/R)$)",
          "Orbital speed ($v_o = \sqrt{GM/r}$) and Escape velocity ($v_e = \sqrt{2GM/R}$)",
          "Kepler's laws of planetary motion ($T^2 \propto a^3$)"
        ],
        commonMistakeWarning: "Gravitational potential hamesha negative hota hai ($V = -GM/r$); infinity par potential zero reference mana jata hai."
      },
      {
        id: "phy-11-11",
        chapter: "Mechanical Properties of Solids (Elasticity)",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Laws of Motion", "Work, Energy & Power"],
        coreSkills: [
          "Stress (Longitudinal, Shear, Volumetric) vs Strain curve",
          "Hooke's Law and Young's Modulus ($Y = \frac{FL}{A\Delta L}$)",
          "Elastic potential energy density ($u = \frac{1}{2}\times\text{stress}\times\text{strain}$)",
          "Elongation of wire under its own weight ($\Delta L = \frac{\rho g L^2}{2Y}$)"
        ],
        commonMistakeWarning: "Wire ke self-weight elongation me effective load $Mg/2$ center of mass par act karta hai, full weight $Mg$ nahi."
      },
      {
        id: "phy-11-12",
        chapter: "Mechanical Properties of Fluids",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Laws of Motion", "Work, Energy & Power"],
        coreSkills: [
          "Hydrostatic pressure variation ($P = P_0 + \rho gh$) and Pascal's principle",
          "Archimedes principle and condition of floatation ($F_B = \rho_{liquid} V_{submerged} g$)",
          "Equation of Continuity ($A_1 v_1 = A_2 v_2$) and Bernoulli's Theorem ($P + \frac{1}{2}\rho v^2 + \rho gh = \text{const}$)",
          "Torricelli's law of efflux, Viscosity (Stokes' Law $F = 6\pi\eta rv$), Surface tension and Capillary rise"
        ],
        commonMistakeWarning: "Excess pressure in air bubble in water is $2T/R$, par soap bubble in air me do surfaces hoti hain isliye excess pressure $4T/R$ hota hai."
      },
      {
        id: "phy-11-13",
        chapter: "Thermal Properties of Matter & Calorimetry",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic Units & Conversions"],
        coreSkills: [
          "Thermal expansion: Linear ($\alpha$), Areal ($\beta = 2\alpha$), Volumetric ($\gamma = 3\alpha$)",
          "Principle of Calorimetry: Heat lost = Heat gained ($Q = mc\Delta T$ and $Q = mL$)",
          "Thermal conduction through composite slabs in series and parallel ($H = \frac{kA\Delta T}{L}$)",
          "Stefan-Boltzmann Law ($E = e\sigma A T^4$), Wien's Displacement Law ($\lambda_m T = b$), Newton's Law of Cooling"
        ],
        commonMistakeWarning: "Stefan's law me temperature hamesha Kelvin (absolute temperature) me substitute karna hota hai, Celsius me nahi."
      },
      {
        id: "phy-11-14",
        chapter: "Thermodynamics",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Thermal Properties of Matter", "Work, Energy & Power"],
        coreSkills: [
          "First Law of Thermodynamics: $dQ = dU + dW$ (Physics convention: $dW = P\,dV$)",
          "Work done in Isothermal ($nRT\ln(V_2/V_1)$) and Adiabatic ($W = \frac{P_1V_1 - P_2V_2}{\gamma - 1}$) processes",
          "Indicator P-V diagram analysis for cyclic heat engine efficiency ($\eta = W_{net}/Q_{in}$)",
          "Second Law, Carnot engine efficiency ($\eta = 1 - T_L/T_H$), and Entropy basics"
        ],
        commonMistakeWarning: "Physics me work done by gas positive ($+P\Delta V$) hota hai jabki Chemistry me work done by gas negative ($-P_{ext}\Delta V$) hota hai."
      },
      {
        id: "phy-11-15",
        chapter: "Kinetic Theory of Gases (KTG)",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Thermodynamics"],
        coreSkills: [
          "Ideal gas law ($PV = nRT = Nk_B T$)",
          "Pressure of ideal gas ($P = \frac{1}{3}\rho v_{rms}^2$) and Speeds ($v_{rms}, v_{avg}, v_{mp}$)",
          "Degrees of freedom ($f$) for monoatomic ($3$), diatomic ($5$), non-linear triatomic ($6$)",
          "Molar specific heats ($C_v = \frac{f}{2}R, C_p = (\frac{f}{2}+1)R, \gamma = 1 + 2/f$)"
        ],
        commonMistakeWarning: "Diatomic gases me high temperature par vibrational degrees of freedom activate hote hain, normal temperature par $f=5$ hi lena hai."
      },
      {
        id: "phy-11-16",
        chapter: "Oscillations (Simple Harmonic Motion)",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Work, Energy & Power", "Circular Motion"],
        coreSkills: [
          "SHM differential equation ($d^2x/dt^2 + \omega^2 x = 0$) and standard solution $x(t) = A\sin(\omega t + \phi)$",
          "Velocity ($v = \omega\sqrt{A^2 - x^2}$) and Acceleration ($a = -\omega^2 x$)",
          "Time period of spring-block systems (series/parallel springs) and simple/physical pendulums",
          "Energy conservation in SHM ($K = \frac{1}{2}m\omega^2(A^2 - x^2), U = \frac{1}{2}m\omega^2 x^2$)"
        ],
        commonMistakeWarning: "Mean position par kinetic energy maximum aur potential energy minimum hoti hai (zaruri nahi zero ho agar reference potential energy di gayi ho)."
      },
      {
        id: "phy-11-17",
        chapter: "Waves & Sound Waves",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Oscillations (SHM)", "Mathematical Tools & Vectors"],
        coreSkills: [
          "Traveling wave equation ($y = A\sin(kx - \omega t + \phi)$), Wave speed $v = \omega/k = \lambda f$",
          "Speed of transverse wave on string ($v = \sqrt{T/\mu}$) and longitudinal wave in medium ($v = \sqrt{B/\rho}$)",
          "Superposition and Standing waves in strings and organ pipes (Closed: $\lambda_n = \frac{4L}{2n-1}$, Open: $\lambda_n = \frac{2L}{n}$)",
          "Beats frequency ($f_b = |f_1 - f_2|$) and Doppler Effect in sound"
        ],
        commonMistakeWarning: "Closed organ pipe me sirf odd harmonics present hoti hain ($1f, 3f, 5f...$), even harmonics exist nahi karti."
      },

      // --- CLASS 12 PHYSICS ---
      {
        id: "phy-12-01",
        chapter: "Electric Charges & Fields",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Mathematical Tools & Vectors", "Laws of Motion", "Gravitation"],
        coreSkills: [
          "Coulomb's Law in vector form with dielectric medium",
          "Electric field integration for continuous charge distributions (Ring, Arc, Line, Disc)",
          "Electric dipole field on axial ($2kp/r^3$) and equatorial ($kp/r^3$) positions; Torque $\vec{\tau} = \vec{p}\times\vec{E}$",
          "Gauss's Law and electric flux calculations ($\Phi = \oint \vec{E}\cdot d\vec{A} = q_{enclosed}/\varepsilon_0$)"
        ],
        commonMistakeWarning: "Gauss's Law har closed surface par valid hai par calculate tabhi kiya ja sakta hai jab charge distribution highly symmetric ho."
      },
      {
        id: "phy-12-02",
        chapter: "Electrostatic Potential & Capacitance",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Electric Charges & Fields", "Work, Energy & Power"],
        coreSkills: [
          "Electric potential due to point charge, dipole, charged conducting/non-conducting spheres",
          "Relation between field and potential ($\vec{E} = -\nabla V = -[\frac{\partial V}{\partial x}\hat{i} + \frac{\partial V}{\partial y}\hat{j} + \frac{\partial V}{\partial z}\hat{k}]$)",
          "Parallel plate capacitance ($C = \frac{\varepsilon_0 A}{d}$) with partial dielectric slab",
          "Combination of capacitors, common potential, and energy loss on connecting ($E_{loss} = \frac{C_1 C_2}{2(C_1+C_2)}(V_1 - V_2)^2$)"
        ],
        commonMistakeWarning: "Agar capacitor battery se connected hai to voltage constant rehta hai, agar disconnect kiya hai to charge constant rehta hai."
      },
      {
        id: "phy-12-03",
        chapter: "Current Electricity",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Electrostatic Potential & Capacitance"],
        coreSkills: [
          "Drift velocity ($v_d = \frac{eE\tau}{m}$) and current density ($j = \sigma E = ne v_d$)",
          "Temperature dependence of resistance ($R_T = R_0(1 + \alpha\Delta T)$)",
          "Kirchhoff's Current Law (KCL) and Voltage Law (KVL) circuit analysis",
          "Wheatstone bridge balance condition, Meter bridge, and Galvanometer to Ammeter/Voltmeter conversion"
        ],
        commonMistakeWarning: "Galvanometer ko ammeter banane ke liye low resistance (shunt) parallel me lagti hai, voltmeter banane ke liye high resistance series me lagti hai."
      },
      {
        id: "phy-12-04",
        chapter: "Moving Charges & Magnetism",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Mathematical Tools & Vectors", "Current Electricity", "Circular Motion"],
        coreSkills: [
          "Lorentz Force ($\vec{F} = q(\vec{E} + \vec{v}\times\vec{B})$) and helical trajectory in magnetic field",
          "Biot-Savart Law for circular loops, straight wires, and solenoids",
          "Ampere's Circuital Law ($\oint \vec{B}\cdot d\vec{l} = \mu_0 I_{enclosed}$)",
          "Force between two parallel current-carrying conductors and Magnetic dipole moment ($\vec{M} = NI\vec{A}$)"
        ],
        commonMistakeWarning: "Magnetic force speed ya kinetic energy change nahi kar sakta kyunki $\vec{F}_B$ hamesha velocity vector $\vec{v}$ ke perpendicular hota hai ($W=0$)."
      },
      {
        id: "phy-12-05",
        chapter: "Magnetism & Matter",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Moving Charges & Magnetism"],
        coreSkills: [
          "Bar magnet as equivalent solenoid and magnetic field lines",
          "Earth's magnetic field components (Declination, Dip angle $\theta$, Horizontal component $B_H$)",
          "Magnetic intensity ($H$), Magnetization ($I$), Magnetic susceptibility ($\chi_m$), Permeability ($\mu_r = 1 + \chi_m$)",
          "Classification of materials: Diamagnetic ($\chi < 0$), Paramagnetic ($\chi > 0$ small), Ferromagnetic ($\chi \gg 1$), Curie's Law"
        ],
        commonMistakeWarning: "Diamagnetic materials temperature-independent hote hain, jabki paramagnetic materials Curie's Law ($\chi \propto 1/T$) follow karte hain."
      },
      {
        id: "phy-12-06",
        chapter: "Electromagnetic Induction (EMI)",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Moving Charges & Magnetism", "Work, Energy & Power"],
        coreSkills: [
          "Magnetic flux ($\Phi_B = \vec{B}\cdot\vec{A}$) and Faraday's Law ($\varepsilon = -d\Phi_B/dt$)",
          "Lenz's Law for determining direction of induced current (conservation of energy)",
          "Motional EMF in translating and rotating conductors ($\varepsilon = Bvl$ and $\varepsilon = \frac{1}{2}B\omega l^2$)",
          "Self-inductance ($L$), Mutual inductance ($M$), and Growth/Decay of current in L-R circuits"
        ],
        commonMistakeWarning: "Lenz's law direction nikalte waqt external flux increase ho raha hai ya decrease, ye check karna zaroori hai taaki induced field opposite ya supporting bane."
      },
      {
        id: "phy-12-07",
        chapter: "Alternating Current (AC)",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Electromagnetic Induction", "Oscillations (SHM)"],
        coreSkills: [
          "Peak, Average, and RMS values ($I_{rms} = I_0/\sqrt{2}$ for sinusoidal wave)",
          "Phasor diagrams for Resistor, Inductor ($X_L = \omega L$), Capacitor ($X_C = 1/\omega C$)",
          "Series LCR circuit impedance ($Z = \sqrt{R^2 + (X_L - X_C)^2}$) and Power factor ($\cos\phi = R/Z$)",
          "Resonance frequency ($\omega_0 = 1/\sqrt{LC}$), Quality factor ($Q = \omega_0 L/R$), and Transformers"
        ],
        commonMistakeWarning: "AC circuits me total voltage arithmetic sum nahi hota; phasor/vector sum ($V = \sqrt{V_R^2 + (V_L - V_C)^2}$) hota hai."
      },
      {
        id: "phy-12-08",
        chapter: "Electromagnetic Waves",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Electromagnetic Induction", "Alternating Current"],
        coreSkills: [
          "Displacement current formula ($I_d = \varepsilon_0 \frac{d\Phi_E}{dt}$) and Maxwell's equations",
          "Wave speed relation ($c = \frac{1}{\sqrt{\mu_0 \varepsilon_0}} = \frac{E_0}{B_0}$)",
          "Poynting vector ($\vec{S} = \frac{1}{\mu_0}\vec{E}\times\vec{B}$) and Radiation pressure",
          "Electromagnetic spectrum order (Radio < Micro < IR < Visible < UV < X-ray < Gamma)"
        ],
        commonMistakeWarning: "$\vec{E}$ aur $\vec{B}$ vectors ek doosre ke perpendicular hote hain aur inka cross product wave propagation direction deta hai."
      },
      {
        id: "phy-12-09",
        chapter: "Ray Optics & Optical Instruments",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Basic Geometry & Trigonometry"],
        coreSkills: [
          "Cartesian sign conventions for mirrors and lenses",
          "Snell's Law and Total Internal Reflection (Critical angle $\sin C = 1/\mu$)",
          "Refraction at spherical surfaces ($\frac{\mu_2}{v} - \frac{\mu_1}{u} = \frac{\mu_2 - \mu_1}{R}$) and Lens Maker's Formula",
          "Prism formula ($\mu = \frac{\sin(\frac{A+\delta_m}{2})}{\sin(A/2)}$), Compound Microscope and Astronomical Telescope magnification"
        ],
        commonMistakeWarning: "Formulae lagate waqt unknown values (jaise image position $v$) ka sign pehle se nahi lagate; wo automatically solve hoke sign ke sath aayega."
      },
      {
        id: "phy-12-10",
        chapter: "Wave Optics",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Waves & Sound Waves", "Ray Optics"],
        coreSkills: [
          "Huygens' principle and wavefront reconstruction",
          "Young's Double Slit Experiment (YDSE): Fringe width ($\beta = \lambda D/d$), path difference ($\Delta x = yd/D$)",
          "YDSE with thin mica/glass slab insertion (Fringe shift $\Delta y = \frac{D}{d}(\mu - 1)t$)",
          "Single slit diffraction central maxima width ($2\lambda D/a$) and Polarization (Brewster's Law $\tan i_p = \mu$)"
        ],
        commonMistakeWarning: "Diffraction me minima condition ($a\sin\theta = n\lambda$) hoti hai, jabki interference me ye maxima condition hoti hai."
      },
      {
        id: "phy-12-11",
        chapter: "Dual Nature of Radiation & Matter",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Work, Energy & Power", "Basic Electrostatics"],
        coreSkills: [
          "Photoelectric effect equation ($h\nu = \phi_0 + K_{max} = \phi_0 + eV_0$)",
          "Stopping potential vs frequency graph slope ($h/e$) and threshold frequency",
          "de Broglie wavelength of matter waves ($\lambda = \frac{h}{p} = \frac{h}{\sqrt{2mE}} = \frac{12.27}{\sqrt{V}}\text{ \AA}$ for electron)",
          "Photon momentum and radiation force calculations"
        ],
        commonMistakeWarning: "Intensity badhane se sirf photocurrent badhta hai; electron ki maximum kinetic energy aur stopping potential sirf frequency par depend karte hain."
      },
      {
        id: "phy-12-12",
        chapter: "Atoms",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Dual Nature of Radiation", "Circular Motion", "Electrostatics"],
        coreSkills: [
          "Rutherford $\alpha$-particle scattering and distance of closest approach",
          "Bohr's quantization postulates: $mvr = \frac{nh}{2\pi}$ and Energy levels ($E_n = -13.6 \frac{Z^2}{n^2}\text{ eV}$)",
          "Rydberg formula for Hydrogen spectrum ($\frac{1}{\lambda} = R Z^2 (\frac{1}{n_1^2} - \frac{1}{n_2^2})$)",
          "Spectral series identification: Lyman (UV), Balmer (Visible), Paschen/Brackett/Pfund (IR)"
        ],
        commonMistakeWarning: "Balmer series visible spectrum me tabhi aati hai jab transitions $n \to 2$ ho; shortest wavelength series limit ($n_2 = \infty$) hoti hai."
      },
      {
        id: "phy-12-13",
        chapter: "Nuclei",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Atoms", "Dual Nature of Radiation"],
        coreSkills: [
          "Nuclear radius formula ($R = R_0 A^{1/3}$ where $R_0 \approx 1.2\text{ fm}$)",
          "Mass defect ($\Delta m = [Z m_p + (A-Z) m_n] - M_{nucleus}$) and Binding Energy per nucleon",
          "Nuclear fission and fusion energy release ($Q = \Delta m \times 931.5\text{ MeV}$)",
          "Radioactive decay law ($N = N_0 e^{-\lambda t}$, $T_{1/2} = \frac{0.693}{\lambda}$)"
        ],
        commonMistakeWarning: "Nuclear density mass number $A$ par depend nahi karti; sabhi nuclei ki density approximately constant ($\approx 2.3 \times 10^{17}\text{ kg/m}^3$) hoti hai."
      },
      {
        id: "phy-12-14",
        chapter: "Semiconductor Electronics: Materials, Devices & Simple Circuits",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Current Electricity", "Atoms"],
        coreSkills: [
          "Intrinsic vs Extrinsic semiconductors (n-type majority electrons, p-type majority holes; $n_e n_h = n_i^2$)",
          "p-n junction diode forward bias (low resistance) and reverse bias (high resistance) V-I characteristics",
          "Zener diode as voltage regulator in reverse breakdown region",
          "Half-wave and Full-wave rectifier efficiency, and Logic gates truth tables (AND, OR, NOT, NAND, NOR, XOR)"
        ],
        commonMistakeWarning: "Zener diode breakdown ke baad voltage across it constant rehta hai chahe input voltage ya load current change ho."
      }
    ],

    chemistry: [
      // --- CLASS 11 CHEMISTRY ---
      {
        id: "chem-11-01",
        chapter: "Some Basic Concepts of Chemistry (Mole Concept)",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic High School Math"],
        coreSkills: [
          "Atomic mass, molecular mass, and molar mass conversions ($n = \text{mass}/\text{molar mass} = N/N_A$)",
          "Empirical formula and Molecular formula calculation from percentage composition",
          "Stoichiometry and Limiting Reagent determination in chemical equations",
          "Concentration terms: Molarity ($M$), Molality ($m$), Mole fraction ($\chi$), Mass %, ppm"
        ],
        commonMistakeWarning: "Molarity temperature par depend karti hai (volume change hone se), jabki Molality temperature-independent hoti hai."
      },
      {
        id: "chem-11-02",
        chapter: "Structure of Atom",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Mole Concept"],
        coreSkills: [
          "Bohr's model calculations for radii, velocity, and energy levels of single-electron species",
          "de Broglie wavelength and Heisenberg's Uncertainty Principle ($\Delta x \cdot \Delta p \ge \frac{h}{4\pi}$)",
          "Quantum numbers ($n, l, m_l, m_s$) and Shapes of $s, p, d$ orbitals",
          "Aufbau principle, Pauli's exclusion principle, and Hund's rule of maximum multiplicity (Exceptions: $Cr, Cu$)"
        ],
        commonMistakeWarning: "Radial nodes formula is $(n - l - 1)$ aur Angular nodes is $l$; Total nodes $= (n - 1)$."
      },
      {
        id: "chem-11-03",
        chapter: "Classification of Elements & Periodicity in Properties",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Structure of Atom"],
        coreSkills: [
          "Modern periodic law and long form of periodic table layout ($s, p, d, f$ blocks)",
          "Periodic trends: Atomic and ionic radii (Isoelectronic species rule: greater nuclear charge = smaller radius)",
          "Ionization Enthalpy trends and anomalies ($IE_1$ of $Be > B$ and $N > O$ due to stable subshells)",
          "Electron Gain Enthalpy (Chlorine has more negative $\Delta_{eg}H$ than Fluorine) and Electronegativity (Pauling scale)"
        ],
        commonMistakeWarning: "Second electron gain enthalpy of oxygen and sulfur hamesha endothermic (positive) hoti hai electron-electron repulsion ki wajah se."
      },
      {
        id: "chem-11-04",
        chapter: "Chemical Bonding & Molecular Structure",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Structure of Atom", "Classification of Elements"],
        coreSkills: [
          "Lewis dot structures, Formal charge calculation, and Resonance structures",
          "VSEPR theory for predicting geometry and shapes with lone pairs ($XeF_4, SF_4, ClF_3, BrF_5$)",
          "Hybridization ($sp, sp^2, sp^3, sp^3d, sp^3d^2$) and Dipole moment ($\mu = q \times d$)",
          "Molecular Orbital Theory (MOT): Energy ordering for $B_2, C_2, N_2$ ($\le 14 e^-$) vs $O_2, F_2$ ($> 14 e^-$), Bond Order and Magnetism"
        ],
        commonMistakeWarning: "MOT me $O_2$ paramagnetic hota hai kyunki isme 2 unpaired electrons $\pi^* 2p_x$ aur $\pi^* 2p_y$ antibonding orbitals me hote hain."
      },
      {
        id: "chem-11-05",
        chapter: "Chemical Thermodynamics",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Mole Concept"],
        coreSkills: [
          "State functions vs Path functions, Extensive vs Intensive properties",
          "First Law of Thermodynamics: $\Delta U = q + w$ (Chemistry convention: $w = -P_{ext}\Delta V$)",
          "Enthalpy changes: $\Delta H = \Delta U + \Delta n_g RT$, Hess's Law of constant heat summation",
          "Entropy ($\Delta S = q_{rev}/T$) and Gibbs Free Energy criterion for spontaneity ($\Delta G = \Delta H - T\Delta S < 0$)"
        ],
        commonMistakeWarning: "Standard enthalpy of formation ($\Delta_f H^\circ$) of elements in their standard reference states (jaise $O_2(g), C(graphite), Br_2(l)$) zero hoti hai."
      },
      {
        id: "chem-11-06",
        chapter: "Equilibrium (Chemical & Ionic)",
        classLevel: "11th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Mole Concept", "Chemical Thermodynamics"],
        coreSkills: [
          "Law of mass action, Equilibrium constants $K_p = K_c(RT)^{\Delta n_g}$, and Le Chatelier's principle",
          "Ostwald's dilution law for weak electrolytes, pH calculations of strong/weak acids and bases",
          "Hydrolysis of salts (Weak acid + Strong base, Strong acid + Weak base, Weak acid + Weak base)",
          "Buffer solutions (Henderson-Hasselbalch equations) and Solubility product ($K_{sp}$) with Common Ion Effect"
        ],
        commonMistakeWarning: "Very dilute acid (jaise $10^{-8}\text{ M } HCl$) ka pH 8 nahi hota; water ke auto-ionization ($10^{-7}\text{ M } H^+$) ko add karke pH $\approx 6.98$ aayega."
      },
      {
        id: "chem-11-07",
        chapter: "Redox Reactions",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Mole Concept", "Chemical Bonding"],
        coreSkills: [
          "Oxidation number calculation rules and exceptional structures ($CrO_5, H_2SO_5, CaOCl_2, Fe_3O_4$)",
          "Balancing redox reactions by Ion-Electron (half-reaction) and Oxidation State methods in acidic/basic media",
          "Disproportionation and Comproportionation reactions identification",
          "Concept of equivalents: $n\text{-factor}$ calculation for oxidizing and reducing agents in various media"
        ],
        commonMistakeWarning: "$KMnO_4$ ka $n\text{-factor}$ acidic medium me 5, neutral/faintly alkaline medium me 3, aur strongly alkaline medium me 1 hota hai."
      },
      {
        id: "chem-11-08",
        chapter: "Organic Chemistry: Some Basic Principles & Techniques (GOC)",
        classLevel: "11th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Chemical Bonding & Molecular Structure"],
        coreSkills: [
          "IUPAC nomenclature of polyfunctional organic compounds",
          "Electronic effects: Inductive (+I/-I), Resonance/Mesomeric (+M/-M), Hyperconjugation, Electromeric",
          "Aromaticity rules (Hückel's Rule $4n+2\\,\pi$ electrons, Anti-aromatic, Non-aromatic)",
          "Stability of reaction intermediates: Carbocations, Carbanions, Free Radicals, and Acidic/Basic strength orders"
        ],
        commonMistakeWarning: "Halogens ($-F, -Cl, -Br, -I$) benzene ring par deactivating hote hain due to dominant $-I$ effect, par ortho/para directing hote hain due to $+M$ effect."
      },
      {
        id: "chem-11-09",
        chapter: "Hydrocarbons",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["GOC (Organic Chemistry Basics)"],
        coreSkills: [
          "Alkanes: Free radical halogenation mechanism, Wurtz reaction, Kolbe's electrolytic synthesis",
          "Alkenes: Electrophilic addition reactions (Markovnikov and Anti-Markovnikov addition with peroxide)",
          "Ozonolysis of alkenes/alkynes ($O_3/Zn-H_2O$) for locating double/triple bonds",
          "Arenes: Electrophilic Aromatic Substitution (Nitration, Halogenation, Sulphonation, Friedel-Crafts Alkylation & Acylation)"
        ],
        commonMistakeWarning: "Peroxide effect (Kharasch effect) sirf $HBr$ ke sath kaam karta hai; $HCl$ aur $HI$ ke sath thermodynamic reasons se Anti-Markovnikov addition nahi hota."
      },

      // --- CLASS 12 CHEMISTRY ---
      {
        id: "chem-12-01",
        chapter: "Solutions",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Mole Concept", "Chemical Thermodynamics"],
        coreSkills: [
          "Henry's Law for gas solubility ($p = K_H \chi$)",
          "Raoult's Law for volatile liquids, Ideal vs Non-ideal solutions (Positive and Negative deviations, Azeotropes)",
          "Colligative properties: Relative lowering of vapor pressure, Elevation of boiling point ($\Delta T_b = i K_b m$), Depression of freezing point ($\Delta T_f = i K_f m$), Osmotic pressure ($\pi = iCRT$)",
          "van 't Hoff factor ($i$) calculations for degree of dissociation ($\alpha = \frac{i-1}{n-1}$) and association ($\alpha = \frac{1-i}{1 - 1/n}$)"
        ],
        commonMistakeWarning: "Colligative properties calculate karte waqt ionic solutes (jaise $NaCl, BaCl_2, K_4[Fe(CN)_6]$) ke liye van 't Hoff factor $i$ multiply karna mat bhoolna."
      },
      {
        id: "chem-12-02",
        chapter: "Electrochemistry",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Redox Reactions", "Chemical Thermodynamics", "Equilibrium"],
        coreSkills: [
          "Galvanic cells, Standard electrode potentials ($E^\circ_{cell} = E^\circ_{cathode} - E^\circ_{anode}$)",
          "Nernst Equation: $E_{cell} = E^\circ_{cell} - \frac{0.0591}{n}\log Q$ at 298 K, and $\Delta G^\circ = -nFE^\circ_{cell} = -RT\ln K$",
          "Conductance in electrolytic solutions: Molar conductivity ($\Lambda_m = \frac{\kappa \times 1000}{M}$), Kohlrausch's Law of independent migration of ions",
          "Faraday's Laws of Electrolysis ($w = ZIt = \frac{E}{96500}It$), Batteries, Fuel cells, and Corrosion"
        ],
        commonMistakeWarning: "Electrochemical series me reduction potentials diye hote hain; equation multiply karne se $E^\circ$ change nahi hota kyunki potential ek intensive property hai."
      },
      {
        id: "chem-12-03",
        chapter: "Chemical Kinetics",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Mole Concept", "Basic Calculus / Integration"],
        coreSkills: [
          "Rate of reaction, Rate law expression, Order vs Molecularity of reaction",
          "Integrated rate equations for Zero order ($[A] = [A]_0 - kt, t_{1/2} = \frac{[A]_0}{2k}$) and First order ($k = \frac{2.303}{t}\log\frac{[A]_0}{[A]}, t_{1/2} = \frac{0.693}{k}$)",
          "Pseudo first-order reactions (Acid-catalyzed hydrolysis of ester)",
          "Arrhenius Equation: $k = A e^{-E_a/RT}$, $\log\frac{k_2}{k_1} = \frac{E_a}{2.303 R}(\frac{1}{T_1} - \frac{1}{T_2})$, and Collision Theory"
        ],
        commonMistakeWarning: "First-order reaction ki half-life ($t_{1/2}$) initial reactant concentration par depend nahi karti, jabki zero-order me $t_{1/2} \propto [A]_0$ hota hai."
      },
      {
        id: "chem-12-04",
        chapter: "The d- and f-Block Elements",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Structure of Atom", "Classification of Elements", "Redox Reactions"],
        coreSkills: [
          "General electronic configurations, Transition elements variable oxidation states, catalytic and magnetic properties",
          "Lanthanoid contraction causes (poor shielding of $4f$ electrons) and consequences ($Zr/Hf$ similar radii)",
          "Preparation, properties, and oxidizing actions of Potassium Dichromate ($K_2Cr_2O_7$) and Potassium Permanganate ($KMnO_4$)",
          "Actinoids electronic configurations and comparison with Lanthanoids"
        ],
        commonMistakeWarning: "$Zn, Cd, Hg$ ko transition elements nahi mana jata kyunki inke ground state aur common oxidation states me $d$-subshell completely filled ($d^{10}$) hota hai."
      },
      {
        id: "chem-12-05",
        chapter: "Coordination Compounds",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Chemical Bonding", "The d- and f-Block Elements"],
        coreSkills: [
          "Werner's coordination theory (Primary vs Secondary valencies)",
          "IUPAC nomenclature of mononuclear coordination complexes",
          "Isomerism: Structural (Ionization, Linkage, Hydrate, Coordination) and Stereoisomerism (Geometrical and Optical)",
          "Valence Bond Theory (VBT) and Crystal Field Theory (CFT): $t_{2g}$ and $e_g$ splitting in octahedral and tetrahedral fields, Spectrochemical series, Spin-only magnetic moment ($\mu = \sqrt{n(n+2)}\text{ BM}$)"
        ],
        commonMistakeWarning: "Tetrahedral complexes me pairing energy splitting energy se zyada hoti hai ($P > \Delta_t$), isliye tetrahedral complexes hamesha high spin bante hain."
      },
      {
        id: "chem-12-06",
        chapter: "Haloalkanes and Haloarenes",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["GOC", "Hydrocarbons"],
        coreSkills: [
          "Nucleophilic substitution mechanisms: $S_N1$ (two steps, carbocation intermediate, racemization) vs $S_N2$ (single step, transition state, Walden inversion)",
          "Elimination reactions (E1 and E2 mechanisms, Saytzeff's vs Hofmann's rule)",
          "Reactions of Haloarenes: Low reactivity towards nucleophilic substitution and electrophilic substitution reactions",
          "Organometallic compounds: Grignard reagent ($RMgX$) preparation and reactions with acidic hydrogen"
        ],
        commonMistakeWarning: "$S_N2$ rate order $1^\circ > 2^\circ > 3^\circ$ hota hai steric hindrance ki wajah se, jabki $S_N1$ rate order $3^\circ > 2^\circ > 1^\circ$ hota hai carbocation stability ki wajah se."
      },
      {
        id: "chem-12-07",
        chapter: "Alcohols, Phenols and Ethers",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Haloalkanes and Haloarenes", "GOC"],
        coreSkills: [
          "Preparation of alcohols: Hydroboration-Oxidation (Anti-Markovnikov hydration), Oxymercuration-Demercuration (Markovnikov hydration)",
          "Lucas Test ($ZnCl_2 + \text{conc. } HCl$) for distinguishing $1^\circ, 2^\circ, 3^\circ$ alcohols",
          "Reactions of Phenol: Electrophilic substitution, Kolbe's reaction (Salicylic acid), Reimer-Tiemann reaction (Salicylaldehyde), Oxidation to Benzoquinone",
          "Ethers: Williamson's ether synthesis ($R-X + R'-O^- Na^+$ where $R-X$ must be $1^\circ$) and Cleavage of ethers with excess $HI$"
        ],
        commonMistakeWarning: "Williamson synthesis me agar $3^\circ$ alkyl halide use kiya to substitution ki jagah elimination ho jayega aur alkene main product banega."
      },
      {
        id: "chem-12-08",
        chapter: "Aldehydes, Ketones and Carboxylic Acids",
        classLevel: "12th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Alcohols, Phenols and Ethers", "GOC"],
        coreSkills: [
          "Nucleophilic addition reactions on carbonyl carbon ($HCN, NaHSO_3, RMgX, NH_2-Z$ derivatives)",
          "Oxidation tests: Tollens' reagent test, Fehling's solution test, Haloform reaction (Iodoform test for $CH_3-C=O$ group)",
          "Named reactions: Aldol condensation, Cross-Aldol condensation, Cannizzaro reaction, Wolff-Kishner reduction, Clemmensen reduction",
          "Carboxylic acids: Acidic strength comparison, Hell-Volhard-Zelinsky (HVZ) reaction, Decarboxylation"
        ],
        commonMistakeWarning: "Cannizzaro reaction sirf un aldehydes me hoti hai jinme $\alpha$-hydrogen absent ho (jaise $HCHO, C_6H_5CHO$)."
      },
      {
        id: "chem-12-09",
        chapter: "Amines (Organic Compounds Containing Nitrogen)",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Aldehydes, Ketones and Carboxylic Acids", "GOC"],
        coreSkills: [
          "Preparation: Gabriel Phthalimide synthesis (only for $1^\circ$ aliphatic amines), Hoffmann Bromamide degradation reaction",
          "Basicity order of amines in gaseous phase ($3^\circ > 2^\circ > 1^\circ > NH_3$) vs Aqueous phase ($-CH_3$: $2^\circ > 1^\circ > 3^\circ$; $-C_2H_5$: $2^\circ > 3^\circ > 1^\circ$)",
          "Chemical tests: Carbylamine test (for $1^\circ$ amines only), Hinsberg test ($C_6H_5SO_2Cl$) to distinguish $1^\circ, 2^\circ, 3^\circ$ amines",
          "Diazonium salts: Preparation, Sandmeyer reaction, Gattermann reaction, and Azo dye coupling reactions"
        ],
        commonMistakeWarning: "Gabriel Phthalimide synthesis se aromatic $1^\circ$ amines (aniline) nahi banaye ja sakte kyunki aryl halides phthalimide anion ke sath nucleophilic substitution nahi karte."
      },
      {
        id: "chem-12-10",
        chapter: "Biomolecules",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["GOC", "Aldehydes & Ketones", "Amines"],
        coreSkills: [
          "Carbohydrates classification: Aldoses, Ketoses, Monosaccharides (Glucose, Fructose structure & mutarotation), Disaccharides (Sucrose non-reducing, Maltose, Lactose), Polysaccharides (Starch, Cellulose, Glycogen)",
          "Amino acids classification (Essential vs Non-essential), Zwitterion structure, Isoelectric point, Peptide linkage",
          "Proteins: Primary, Secondary ($\alpha$-helix, $\beta$-pleated sheet), Tertiary, Quaternary structures, and Denaturation",
          "Nucleic acids: Nucleosides, Nucleotides, Phosphodiester linkage, DNA double helix structure (A-T, G-C base pairing), RNA types"
        ],
        commonMistakeWarning: "Sucrose ek non-reducing sugar hai kyunki glucose aur fructose dono ke reducing groups (hemiacetal/hemiketal carbons) glycosidic bond me involved hote hain."
      }
    ],

    math: [
      // --- CLASS 11 MATH ---
      {
        id: "math-11-01",
        chapter: "Sets & Relations",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic High School Algebra"],
        coreSkills: [
          "Set operations: Union, Intersection, Difference, Complement, De Morgan's Laws",
          "Cardinality problems: $n(A\cup B) = n(A) + n(B) - n(A\cap B)$",
          "Cartesian product of sets and subsets of $A \times B$",
          "Types of relations: Reflexive ($aRa$), Symmetric ($aRb \implies bRa$), Transitive ($aRb \land bRc \implies aRc$), Equivalence relations"
        ],
        commonMistakeWarning: "Relation me transitive condition tabhi check karni hoti hai jab $(a,b)$ aur $(b,c)$ dono present hon; agar $(b,c)$ exist hi nahi karta to relation vacuously transitive hoti hai."
      },
      {
        id: "math-11-02",
        chapter: "Functions",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Sets & Relations"],
        coreSkills: [
          "Finding Domain and Range of algebraic, rational, trigonometric, exponential, and logarithmic functions",
          "Standard functions: Modulus $|x|$, Greatest Integer Function $[x]$, Fractional Part $\{x\}$, Signum function",
          "Types of mappings: One-One (Injective), Many-One, Onto (Surjective), Into, Bijective mappings",
          "Composite functions ($f(g(x))$) and Invertible functions ($f^{-1}(x)$)"
        ],
        commonMistakeWarning: "Even and Odd function test: $f(-x) = f(x)$ (Even) or $-f(x)$ (Odd); agar function even hai to uska inverse exist nahi karta kyunki wo many-one hota hai."
      },
      {
        id: "math-11-03",
        chapter: "Complex Numbers & Quadratic Equations",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Basic Algebra", "Trigonometry"],
        coreSkills: [
          "Algebra of complex numbers, Modulus ($|z|$), Conjugate ($\bar{z}$), Argument ($\arg(z)$), Polar and Euler forms ($z = r e^{i\theta}$)",
          "De Moivre's Theorem and Cube roots of unity ($1, \omega, \omega^2$; $1+\omega+\omega^2=0, \omega^3=1$)",
          "Quadratic equations: Nature of roots ($D = b^2 - 4ac$), Vieta's formulas ($\alpha+\beta, \alpha\beta$), Transformation of roots",
          "Location of roots conditions on real number line and Common root conditions"
        ],
        commonMistakeWarning: "Square root property $\sqrt{a}\sqrt{b} = \sqrt{ab}$ tabhi valid hai jab $a$ aur $b$ dono simultaneously negative na hon."
      },
      {
        id: "math-11-04",
        chapter: "Linear Inequalities",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic Algebra"],
        coreSkills: [
          "Wavy Curve Method (Method of Intervals) for polynomial and rational inequalities",
          "Inequalities involving absolute values ($|x-a| < r \implies a-r < x < a+r$)",
          "System of linear inequalities in two variables and graphical feasible region determination"
        ],
        commonMistakeWarning: "Inequality ko variable quantity se cross-multiply tab tak nahi karte jab tak uska sign (+ve ya -ve) guaranteed maloom na ho."
      },
      {
        id: "math-11-05",
        chapter: "Permutations & Combinations (PnC)",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Basic Algebra", "Factorials"],
        coreSkills: [
          "Fundamental principles of counting: Addition and Multiplication rules",
          "Arrangements ($^n P_r = \frac{n!}{(n-r)!}$) vs Selections ($^n C_r = \frac{n!}{r!(n-r)!}$)",
          "Permutations with repetitions, Circular permutations ($(n-1)!$)",
          "Division and distribution of identical objects (Beggar's method / Stars and Bars: $^{n+r-1}C_{r-1}$) and Distinct objects, Derangements ($D_n$)"
        ],
        commonMistakeWarning: "Identical objects ko arrange karne ka sirf 1 way hota hai, jabki distinct objects ko arrange karne ke $n!$ ways hote hain."
      },
      {
        id: "math-11-06",
        chapter: "Binomial Theorem",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Permutations & Combinations"],
        coreSkills: [
          "Binomial expansion: $(x+y)^n = \sum_{r=0}^n {^n C_r} x^{n-r} y^r$, General term $T_{r+1} = {^n C_r} x^{n-r} y^r$",
          "Middle term(s) and Term independent of $x$",
          "Properties and sum of binomial coefficients ($C_0 + C_1 + ... + C_n = 2^n$)",
          "Divisibility, remainder problems, and fractional part applications using binomial theorem"
        ],
        commonMistakeWarning: "Remainder nikalte waqt remainder hamesha non-negative integer ($0 \le R < \text{divisor}$) hona chahiye."
      },
      {
        id: "math-11-07",
        chapter: "Sequences & Series",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Basic Algebra", "Quadratic Equations"],
        coreSkills: [
          "Arithmetic Progression (AP): $T_n = a + (n-1)d, S_n = \frac{n}{2}[2a + (n-1)d]$, Arithmetic Mean (AM)",
          "Geometric Progression (GP): $T_n = ar^{n-1}, S_n = \frac{a(r^n - 1)}{r-1}$, Infinite GP sum ($S_\infty = \frac{a}{1-r}$ for $|r|<1$), Geometric Mean (GM)",
          "Arithmetico-Geometric Progression (AGP) summation technique",
          "Special series sum ($\sum n, \sum n^2, \sum n^3$), Telescoping series ($V_n - V_{n-1}$ method), and $AM \ge GM \ge HM$ inequality"
        ],
        commonMistakeWarning: "$AM \ge GM$ inequality tabhi lag sakti hai jab saare terms strictly positive real numbers hon."
      },
      {
        id: "math-11-08",
        chapter: "Straight Lines",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Coordinate Geometry Basics", "Trigonometry"],
        coreSkills: [
          "Slope formula ($m = \tan\theta$), various forms of line (Point-slope, Two-point, Slope-intercept, Intercept, Normal form)",
          "Angle between two lines ($\tan\theta = |\frac{m_1 - m_2}{1 + m_1 m_2}|$), Parallel ($m_1=m_2$) and Perpendicular ($m_1 m_2 = -1$) conditions",
          "Distance of a point from a line ($d = \frac{|a x_1 + b y_1 + c|}{\sqrt{a^2 + b^2}}$) and Distance between parallel lines",
          "Family of lines ($L_1 + \lambda L_2 = 0$), Angle bisector equations, Foot of perpendicular, and Image of a point"
        ],
        commonMistakeWarning: "Perpendicular lines condition $m_1 m_2 = -1$ tab fail ho jati hai jab lines coordinate axes ke parallel hon ($x=c$ and $y=k$)."
      },
      {
        id: "math-11-09",
        chapter: "Conic Sections (Circles, Parabola, Ellipse, Hyperbola)",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Straight Lines", "Quadratic Equations"],
        coreSkills: [
          "Circle equation ($x^2 + y^2 + 2gx + 2fy + c = 0$), Tangent equation ($T=0$), Chord of contact, Family of circles",
          "Standard Parabola ($y^2 = 4ax$): Focus, Directrix, Latus Rectum, Parametric coordinates ($at^2, 2at$), Tangents and Normals",
          "Ellipse ($\frac{x^2}{a^2} + \frac{y^2}{b^2} = 1$): Eccentricity ($b^2 = a^2(1-e^2)$), Foci, Directrices, Parametric form ($a\cos\theta, b\sin\theta$)",
          "Hyperbola ($\frac{x^2}{a^2} - \frac{y^2}{b^2} = 1$): Eccentricity ($b^2 = a^2(e^2-1)$), Asymptotes, Rectangular hyperbola ($xy = c^2$)"
        ],
        commonMistakeWarning: "Common tangents nikalte waqt do conics ki tangent equations ko equate karke slope $m$ solve karna hota hai."
      },
      {
        id: "math-11-10",
        chapter: "Introduction to Three Dimensional Geometry (3D Basics)",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Coordinate Geometry (2D)"],
        coreSkills: [
          "Coordinate axes and coordinate planes in 3D space (8 octants)",
          "Distance formula between two points: $d = \sqrt{(x_2-x_1)^2 + (y_2-y_1)^2 + (z_2-z_1)^2}$",
          "Section formula (Internal and External division) and Centroid of triangle/tetrahedron"
        ],
        commonMistakeWarning: "3D me octants ke signs ($\pm x, \pm y, \pm z$) 2D quadrants se different hote hain, visualization careful honi chahiye."
      },
      {
        id: "math-11-11",
        chapter: "Limits & Derivatives (Calculus Basics)",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Functions", "Trigonometry"],
        coreSkills: [
          "Intuitive idea of limits, Left Hand Limit (LHL) and Right Hand Limit (RHL)",
          "Standard algebraic and trigonometric limits: $\lim_{x\to 0}\frac{\sin x}{x} = 1, \lim_{x\to 0}\frac{\tan x}{x} = 1, \lim_{x\to a}\frac{x^n - a^n}{x-a} = n a^{n-1}$",
          "Derivative definition as limit of difference quotient: $f'(x) = \lim_{h\to 0}\frac{f(x+h) - f(x)}{h}$",
          "Derivatives of standard polynomial, trigonometric, exponential, logarithmic functions"
        ],
        commonMistakeWarning: "Trigonometric limits ($\lim_{x\to 0}\frac{\sin x}{x} = 1$) tabhi valid hoti hain jab angle $x$ Radians me measure kiya gaya ho, Degrees me nahi."
      },
      {
        id: "math-11-12",
        chapter: "Statistics",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic Arithmetic & Mean"],
        coreSkills: [
          "Measures of central tendency: Mean, Median, Mode for ungrouped and grouped data",
          "Measures of dispersion: Mean Deviation about mean and median",
          "Variance ($\sigma^2 = \frac{\sum x_i^2}{N} - (\bar{x})^2$) and Standard Deviation ($\sigma = \sqrt{\text{Variance}}$)",
          "Effect of origin shift ($x_i \to x_i + c$: no change in $\sigma$) and scale change ($x_i \to k x_i$: $\sigma_{new} = |k|\sigma$)"
        ],
        commonMistakeWarning: "Agar data me har term me constant $c$ add/subtract karein to Variance unchanged rehta hai, par agar multiply karein to Variance $k^2$ times ho jata hai."
      },
      {
        id: "math-11-13",
        chapter: "Probability (Class 11 Basics)",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Sets & Relations", "Permutations & Combinations"],
        coreSkills: [
          "Sample space, Events (Mutually exclusive, Exhaustive, Independent events)",
          "Axiomatic definition of probability: $P(A\cup B) = P(A) + P(B) - P(A\cap B)$",
          "Probability calculation using Permutations and Combinations methods"
        ],
        commonMistakeWarning: "Mutually exclusive events ($A\cap B = \phi$) aur Independent events ($P(A\cap B) = P(A)P(B)$) me difference samajhna zaroori hai."
      },

      // --- CLASS 12 MATH ---
      {
        id: "math-12-01",
        chapter: "Relations & Functions (Class 12)",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Sets & Relations", "Functions (Class 11)"],
        coreSkills: [
          "Equivalence classes and partitions of sets",
          "Bijective functions condition for invertibility ($f(x)$ must be both 1-1 and Onto)",
          "Composition of functions ($f\circ g(x)$) domain and range restrictions",
          "Binary operations and their properties (Commutative, Associative, Identity element, Inverses)"
        ],
        commonMistakeWarning: "Inverse function $f^{-1}(x)$ find karne se pehle ye prove karna zaroori hai ki function given domain/codomain me Bijective hai."
      },
      {
        id: "math-12-02",
        chapter: "Inverse Trigonometric Functions (ITF)",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Trigonometry", "Relations & Functions (Class 12)"],
        coreSkills: [
          "Principal value branches and graphs of $\sin^{-1}x, \cos^{-1}x, \tan^{-1}x, \cot^{-1}x, \sec^{-1}x, \csc^{-1}x$",
          "Properties: $\sin^{-1}(\sin x) = x$ (only if $x \in [-\pi/2, \pi/2]$) and piecewise graphs for outside domain",
          "Complementary identities: $\sin^{-1}x + \cos^{-1}x = \pi/2, \tan^{-1}x + \cot^{-1}x = \pi/2$",
          "Addition and subtraction formulae for $\tan^{-1}x \pm \tan^{-1}y$"
        ],
        commonMistakeWarning: "$\sin^{-1}(\sin \frac{2\pi}{3}) \ne \frac{2\pi}{3}$ kyunki $\frac{2\pi}{3}$ principal range $[-\pi/2, \pi/2]$ se bahar hai; correct answer $\pi - \frac{2\pi}{3} = \frac{\pi}{3}$ hai."
      },
      {
        id: "math-12-03",
        chapter: "Matrices",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Basic Algebra"],
        coreSkills: [
          "Matrix operations: Addition, Scalar multiplication, Matrix multiplication ($A_{m\times n} B_{n\times p} = C_{m\times p}$)",
          "Transpose of matrix ($A^T$), Symmetric ($A^T = A$) and Skew-Symmetric ($A^T = -A$) matrices",
          "Orthogonal matrices ($A A^T = I$), Idempotent ($A^2=A$), Involutory ($A^2=I$), Nilpotent matrices",
          "Elementary row/column operations and Cayley-Hamilton Theorem ($|A - \lambda I| = 0$)"
        ],
        commonMistakeWarning: "Matrix multiplication non-commutative hota hai ($AB \ne BA$ in general) aur $AB = O$ hone ke liye zaruri nahi ki $A=O$ ya $B=O$ ho."
      },
      {
        id: "math-12-04",
        chapter: "Determinants",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Matrices"],
        coreSkills: [
          "Determinant expansion using minors and cofactors",
          "Properties of determinants (Row/Column transformations, $|AB| = |A||B|, |kA| = k^n |A|$)",
          "Adjoint of matrix ($\text{adj}(A)$) and Inverse ($A^{-1} = \frac{\text{adj}(A)}{|A|}$)",
          "Properties of adjoint: $A(\text{adj } A) = |A| I, |\text{adj } A| = |A|^{n-1}, |\text{adj}(\text{adj } A)| = |A|^{(n-1)^2}$",
          "Cramer's Rule for system of linear equations (Unique solution, Infinitely many solutions, No solution)"
        ],
        commonMistakeWarning: "System of equations me infinite solutions hone ke liye $\Delta = \Delta_x = \Delta_y = \Delta_z = 0$ hona chahiye, par agar $\Delta = 0$ ho aur koi ek $\Delta_i \ne 0$ ho to No Solution hota hai."
      },
      {
        id: "math-12-05",
        chapter: "Continuity & Differentiability",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Limits & Derivatives (Class 11)", "Functions"],
        coreSkills: [
          "Continuity test: $\lim_{x\to a^-} f(x) = \lim_{x\to a^+} f(x) = f(a)$",
          "Differentiability test: Left Hand Derivative (LHD) = Right Hand Derivative (RHD)",
          "Differentiation rules: Chain rule, Implicit differentiation, Logarithmic differentiation, Parametric differentiation, Second-order derivatives",
          "Rolle's Theorem and Lagrange's Mean Value Theorem (LMVT)"
        ],
        commonMistakeWarning: "Differentiability implies continuity, par continuity does not imply differentiability (e.g., $f(x) = |x|$ continuous hai par $x=0$ par differentiable nahi hai)."
      },
      {
        id: "math-12-06",
        chapter: "Application of Derivatives (AOD)",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Continuity & Differentiability"],
        coreSkills: [
          "Rate of change of quantities ($dy/dt = \frac{dy}{dx}\cdot\frac{dx}{dt}$)",
          "Increasing and Decreasing functions: Strictly increasing ($f'(x) > 0$), Monotonicity intervals",
          "Maxima and Minima: First derivative test and Second derivative test ($f'(x)=0$ and $f''(x) < 0$ for maximum)",
          "Global/Absolute Maxima & Minima in closed intervals $[a,b]$, and Word problem optimizations"
        ],
        commonMistakeWarning: "Closed interval $[a,b]$ me global maxima/minima nikalte waqt critical points ke sath-sath boundary points ($x=a$ aur $x=b$) par bhi value check karni hoti hai."
      },
      {
        id: "math-12-07",
        chapter: "Indefinite Integrals",
        classLevel: "12th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Continuity & Differentiability", "Inverse Trigonometric Functions"],
        coreSkills: [
          "Standard integration formulas and Integration by Substitution",
          "Integration using Trigonometric identities and algebraic manipulations",
          "Integration by Parts ($ILATE$ rule: $\int u v\,dx = u\int v\,dx - \int (u'\int v\,dx)dx$)",
          "Integration by Partial Fractions and Special standard integral forms ($\int \frac{dx}{x^2 \pm a^2}, \int \frac{dx}{\sqrt{a^2 - x^2}}, \int e^x[f(x)+f'(x)]dx = e^x f(x) + C$)"
        ],
        commonMistakeWarning: "Integration by parts me $ILATE$ order follow na karna aur algebraic substitution ke baad $dx$ ko $dt$ me convert karna bhool jana."
      },
      {
        id: "math-12-08",
        chapter: "Definite Integrals",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Indefinite Integrals"],
        coreSkills: [
          "Fundamental Theorem of Calculus ($\int_a^b f(x)dx = F(b) - F(a)$)",
          "King's Property: $\int_a^b f(x)dx = \int_a^b f(a+b-x)dx$",
          "Periodic properties ($\int_0^{nT} f(x)dx = n\int_0^T f(x)dx$) and Even/Odd function properties ($\int_{-a}^a f(x)dx = 2\int_0^a f(x)dx$ if even, $0$ if odd)",
          "Leibniz Rule for differentiation of integrals ($\frac{d}{dx}\int_{\psi(x)}^{\phi(x)} f(t)dt = f(\phi(x))\phi'(x) - f(\psi(x))\psi'(x)$) and Definite integral as limit of sum"
        ],
        commonMistakeWarning: "Substitution method lagate waqt integration limits ko variable ke corresponding change karna zaroori hai."
      },
      {
        id: "math-12-09",
        chapter: "Application of Integrals (Area Under Curves)",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Definite Integrals", "Conic Sections"],
        coreSkills: [
          "Area bounded by curve $y = f(x)$, $x$-axis and ordinates $x=a, x=b$ ($\text{Area} = \int_a^b |y|\,dx$)",
          "Area bounded between two intersecting curves $y = f(x)$ and $y = g(x)$ ($\text{Area} = \int_a^b |f(x) - g(x)|\,dx$)",
          "Symmetry identification in parabolas, circles, ellipses to simplify integration"
        ],
        commonMistakeWarning: "Agar curve $x$-axis ke neeche jati hai to definite integral negative aayega, par Area hamesha positive magnitude ($|y|$) hota hai."
      },
      {
        id: "math-12-10",
        chapter: "Differential Equations",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Indefinite Integrals", "Application of Derivatives"],
        coreSkills: [
          "Order and Degree of differential equation (Degree tabhi define hoti hai jab equation derivatives me polynomial ho)",
          "Variable Separable method and equations reducible to variable separable",
          "Homogeneous differential equations ($dy/dx = f(y/x)$ substitution $y = vx$)",
          "First-order Linear Differential Equations ($\frac{dy}{dx} + P(x)y = Q(x)$) using Integrating Factor ($IF = e^{\int P\,dx}$, Solution: $y(IF) = \int Q(IF)dx + C$)"
        ],
        commonMistakeWarning: "Linear differential equation standard form me honi chahiye jahan $dy/dx$ ka coefficient 1 ho; agar $x \frac{dy}{dx} + y = Q$ hai to pehle $x$ se divide karna hoga."
      },
      {
        id: "math-12-11",
        chapter: "Vector Algebra",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Mathematical Tools & Vectors (Class 11)"],
        coreSkills: [
          "Dot product ($\vec{a}\cdot\vec{b} = |\vec{a}||\vec{b}|\cos\theta$), Projection of vector ($\frac{\vec{a}\cdot\vec{b}}{|\vec{b}|}$), and Orthogonality ($\vec{a}\cdot\vec{b} = 0$)",
          "Cross product ($\vec{a}\times\vec{b} = |\vec{a}||\vec{b}|\sin\theta\,\hat{n}$) and Area of triangle/parallelogram",
          "Scalar Triple Product (Box product $[\vec{a}\,\vec{b}\,\vec{c}] = \vec{a}\cdot(\vec{b}\times\vec{c})$) representing volume of parallelepiped; Coplanarity condition ($[\vec{a}\,\vec{b}\,\vec{c}] = 0$)",
          "Vector Triple Product expansion ($\vec{a}\times(\vec{b}\times\vec{c}) = (\vec{a}\cdot\vec{c})\vec{b} - (\vec{a}\cdot\vec{b})\vec{c}$)"
        ],
        commonMistakeWarning: "Box product me dot aur cross interchange kiye ja sakte hain ($\vec{a}\cdot(\vec{b}\times\vec{c}) = (\vec{a}\times\vec{b})\cdot\vec{c}$), par cyclic order maintain hona chahiye."
      },
      {
        id: "math-12-12",
        chapter: "Three Dimensional Geometry (3D)",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Vector Algebra", "Straight Lines (2D)"],
        coreSkills: [
          "Direction Cosines ($l, m, n$ where $l^2+m^2+n^2=1$) and Direction Ratios ($a, b, c$)",
          "Vector and Cartesian equations of line passing through point and parallel to vector ($\vec{r} = \vec{a} + \lambda\vec{b}$, $\frac{x-x_1}{a} = \frac{y-y_1}{b} = \frac{z-z_1}{c}$)",
          "Angle between two lines ($\cos\theta = \frac{\vec{b}_1\cdot\vec{b}_2}{|\vec{b}_1||\vec{b}_2|}$)",
          "Shortest distance between two skew lines ($d = \frac{|(\vec{a}_2 - \vec{a}_1)\cdot(\vec{b}_1\times\vec{b}_2)|}{|\vec{b}_1\times\vec{b}_2|}$) and Condition for intersecting lines"
        ],
        commonMistakeWarning: "Parallel lines ke beech shortest distance formula ($d = \frac{|(\vec{a}_2 - \vec{a}_1)\times\vec{b}|}{|\vec{b}|}$) skew lines ke formula se different hota hai."
      },
      {
        id: "math-12-13",
        chapter: "Probability (Class 12 - Bayes' Theorem & Distributions)",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Probability (Class 11)", "PnC", "Sets"],
        coreSkills: [
          "Conditional Probability ($P(A|B) = \frac{P(A\cap B)}{P(B)}$) and Multiplication theorem",
          "Independent events ($P(A\cap B) = P(A)P(B)$)",
          "Law of Total Probability ($P(A) = \sum P(E_i)P(A|E_i)$) and Bayes' Theorem ($P(E_i|A) = \frac{P(E_i)P(A|E_i)}{\sum P(E_j)P(A|E_j)}$)",
          "Random variable probability distribution, Mean / Expectation ($E(X) = \sum x_i p_i$) and Variance ($\text{Var}(X) = E(X^2) - [E(X)]^2$)"
        ],
        commonMistakeWarning: "Bayes' theorem lagate waqt hypotheses $E_1, E_2...$ pairwise mutually exclusive aur exhaustive hone chahiye ($\sum P(E_i) = 1$)."
      }
    ]
  },

  neet: {
    biology: [
      // --- CLASS 11 BOTANY & ZOOLOGY ---
      {
        id: "bio-11-01",
        chapter: "The Living World",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Class 10 General Science"],
        coreSkills: [
          "Defining characteristics of living organisms (Metabolism, Cellular organization, Consciousness are defining; Growth & Reproduction are not)",
          "Binomial Nomenclature rules by Linnaeus (Latin names, Italics, Generic name capitalized, specific epithet lowercase)",
          "Taxonomic hierarchy sequence: Kingdom $\to$ Phylum/Division $\to$ Class $\to$ Order $\to$ Family $\to$ Genus $\to$ Species",
          "NCERT examples table: Man, Housefly, Mango, Wheat taxonomic categories"
        ],
        commonMistakeWarning: "Order 'Polymoniales' family 'Solanaceae' aur 'Convolvulaceae' ko include karta hai floral characters ke basis par; families ko order se confuse mat karna."
      },
      {
        id: "bio-11-02",
        chapter: "Biological Classification",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["The Living World"],
        coreSkills: [
          "Five Kingdom Classification by R.H. Whittaker (Cell structure, Thallus organization, Mode of nutrition, Reproduction, Phylogenetic relationships)",
          "Kingdom Monera: Archaebacteria (Methanogens, Halophiles, Thermoacidophiles) and Eubacteria (Cyanobacteria heterocysts, Mycoplasma lack cell wall)",
          "Kingdom Protista: Chrysophytes (Diatomaceous earth), Dinoflagellates (Red tide by Gonyaulax), Euglenoids, Slime moulds, Protozoans",
          "Kingdom Fungi: Classes Phycomycetes, Ascomycetes, Basidiomycetes, Deuteromycetes (spore types and asexual/sexual reproduction)",
          "Viruses (Capsid, genetic material either DNA or RNA), Viroids (free RNA, lack protein coat, low molecular weight), Prions, Lichens"
        ],
        commonMistakeWarning: "Deuteromycetes ko 'Fungi Imperfecti' kehte hain kyunki inme sexual reproduction absent ya unknown hota hai; jaise hi sexual stage discover hoti hai ye Ascomycetes ya Basidiomycetes me transfer ho jate hain."
      },
      {
        id: "bio-11-03",
        chapter: "Plant Kingdom",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Biological Classification"],
        coreSkills: [
          "Algae classification: Chlorophyceae (Chlorophyll a, b, starch), Phaeophyceae (Chlorophyll a, c, fucoxanthin, mannitol/laminarin), Rhodophyceae (Chlorophyll a, d, r-phycoerythrin, floridean starch)",
          "Bryophytes: Amphibians of plant kingdom, Gametophyte dominant, Mosses vs Liverworts, Gemma cups, Protonema stage",
          "Pteridophytes: Vascular cryptogams, Sporophyte dominant, Heterospory (Selaginella, Salvinia leading to seed habit), Prothallus",
          "Gymnosperms: Naked seeds, Mycorrhiza in Pinus, Coralloid roots with cyanobacteria in Cycas, Non-motile pollen tube siphonogamy"
        ],
        commonMistakeWarning: "Volvox colonial hai, Chlamydomonas unicellular hai, aur Kelps massive filamentous brown algae hain; examples direct NCERT se yaad hone chahiye."
      },
      {
        id: "bio-11-04",
        chapter: "Animal Kingdom",
        classLevel: "11th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Biological Classification"],
        coreSkills: [
          "Basis of classification: Levels of organization, Symmetry (Radial vs Bilateral), Diploblastic/Triploblastic, Coelom (Acoelomate, Pseudocoelomate in Aschelminthes, Coelomate), Segmentation, Notochord",
          "Non-Chordata phyla: Porifera (Choanocytes, water canal system), Cnidaria (Cnidoblasts, Metagenesis in Obelia), Ctenophora (Comb plates, Bioluminescence), Platyhelminthes (Flame cells), Aschelminthes, Annelida (Metameres, Nephridia), Arthropoda (Chitinous exoskeleton, Jointed appendages), Mollusca (Radula, Mantle cavity), Echinodermata (Water vascular system, Radial adult/Bilateral larva), Hemichordata (Proboscis gland)",
          "Chordata: Urochordata, Cephalochordata, Vertebrata (Cyclostomata, Chondrichthyes vs Osteichthyes, Amphibia, Reptilia, Aves pneumatic bones, Mammalia mammary glands)"
        ],
        commonMistakeWarning: "Echinoderms me adult radially symmetrical hota hai jabki larva bilaterally symmetrical hota hai; Aschelminthes only pseudocoelomate phylum hai."
      },
      {
        id: "bio-11-05",
        chapter: "Morphology of Flowering Plants",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Plant Kingdom"],
        coreSkills: [
          "Root modifications (Prop, Stilt, Pneumatophores in Rhizophora) and Stem modifications (Tendrils, Thorns, Phylloclade in Opuntia, Runners, Rhizome)",
          "Leaf venation (Reticulate vs Parallel) and Phyllotaxy (Alternate, Opposite, Whorled)",
          "Inflorescence (Racemose vs Cymose) and Flower parts (Hypogynous superior ovary, Perigynous half-inferior, Epigynous inferior ovary)",
          "Aestivation types (Valvate, Twisted, Imbricate, Vexillary in Pea) and Placentation types (Marginal, Axile, Parietal, Free central, Basal)",
          "Floral diagrams, Floral formulas, and Family characteristics of Solanaceae, Fabaceae, Liliaceae"
        ],
        commonMistakeWarning: "Free-central placentation Dianthus aur Primrose me hoti hai (septa absent); Basal placentation Sunflower aur Marigold me hoti hai (single ovule at base)."
      },
      {
        id: "bio-11-06",
        chapter: "Anatomy of Flowering Plants",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Morphology of Flowering Plants"],
        coreSkills: [
          "Meristematic tissues (Apical, Intercalary, Lateral) and Permanent tissues (Parenchyma, Collenchyma, Sclerenchyma sclereids/fibres)",
          "Complex tissues: Xylem (Tracheids, Vessels, Xylem parenchyma, Xylem fibres) and Phloem (Sieve tubes, Companion cells, Phloem parenchyma, Phloem fibres)",
          "Internal structure: Dicot vs Monocot Root, Dicot vs Monocot Stem (Dicot open vascular bundles in ring vs Monocot closed scattered with sclerenchymatous sheath), Dicot vs Monocot Leaf (Dorsiventral vs Isobilateral with bulliform cells)",
          "Secondary Growth in dicot stem: Vascular cambium activity (Spring wood vs Autumn wood, Heartwood non-functional vs Sapwood functional) and Cork cambium (Phellogen, Phellem, Phelloderm)"
        ],
        commonMistakeWarning: "Monocots me secondary growth absent hoti hai kyunki unke vascular bundles closed hote hain (cambium absent)."
      },
      {
        id: "bio-11-07",
        chapter: "Structural Organisation in Animals (Animal Tissues & Frog)",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Animal Kingdom"],
        coreSkills: [
          "Epithelial tissue types: Simple squamous (diffusion boundary - blood vessels/alveoli), Cuboidal (PCT of nephron), Columnar, Ciliated (fallopian tubes/bronchioles), Compound epithelium",
          "Cell junctions: Tight junctions (prevent leakage), Adhering junctions (cementing), Gap junctions (rapid ion communication)",
          "Connective tissue: Loose (Areolar, Adipose), Dense (Tendons attach muscle to bone, Ligaments attach bone to bone), Specialized (Cartilage, Bone, Blood)",
          "Muscle tissue (Skeletal striated voluntary, Smooth non-striated involuntary, Cardiac striated involuntary intercalated discs) and Neural tissue",
          "Frog morphology and anatomy: Digestive, Respiratory, Circulatory (3-chambered heart, hepatic portal), Excretory (Ureotelic), Reproductive system"
        ],
        commonMistakeWarning: "Ligament 'Bone to Bone' connect karta hai jabki Tendon 'Muscle to Bone' connect karta hai."
      },
      {
        id: "bio-11-08",
        chapter: "Cell: The Unit of Life",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Class 10 Basic Biology"],
        coreSkills: [
          "Cell theory by Schleiden & Schwann, modified by Rudolf Virchow ('Omnis cellula-e cellula')",
          "Prokaryotic cell: Mesosomes (respiration/wall formation), Plasmids, Inclusion bodies, Gram positive vs negative envelope",
          "Eukaryotic cell membrane: Fluid Mosaic Model by Singer & Nicolson (phospholipid bilayer with integrated proteins)",
          "Endomembrane system: Endoplasmic Reticulum (RER protein synthesis, SER lipid/steroid synthesis), Golgi Apparatus (packaging and glycosidation), Lysosomes (acid hydrolases), Vacuoles",
          "Mitochondria and Chloroplasts (70S ribosomes, circular DNA, endosymbiotic origin), Ribosomes (80S eukaryotic, 70S prokaryotic), Cytoskeleton ($9+2$ cilia/flagella, $9+0$ centrioles)"
        ],
        commonMistakeWarning: "Peroxisomes, Chloroplasts, aur Mitochondria Endomembrane System ka part nahi hain kyunki inke functions ER/Golgi se coordinated nahi hote."
      },
      {
        id: "bio-11-09",
        chapter: "Biomolecules",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Cell: The Unit of Life", "Basic Organic Chemistry"],
        coreSkills: [
          "Biomacromolecules (acid-insoluble pool: proteins, nucleic acids, polysaccharides; lipids in insoluble pool due to membrane vesicles)",
          "Proteins: Amino acids structure (Zwitterion), Peptide bonds, Primary, Secondary ($\alpha$-helix, $\beta$-pleated), Tertiary (functional 3D with disulfide bonds), Quaternary structure",
          "Nucleic acids: Nucleosides, Nucleotides, Watson-Crick double helix DNA model with phosphodiester and hydrogen bonds",
          "Enzyme properties: Activation energy reduction, Active site, Factors affecting enzyme activity ($T, pH, [S]$), $K_m$ value, Competitive inhibition (Malonate inhibits Succinic dehydrogenase)"
        ],
        commonMistakeWarning: "Lipids strictly macromolecules nahi hote kyunki unka molecular weight $< 800\text{ Da}$ hota hai, par membrane vesicles banne ki wajah se wo acid-insoluble fraction me retain hote hain."
      },
      {
        id: "bio-11-10",
        chapter: "Cell Cycle and Cell Division",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Cell: The Unit of Life"],
        coreSkills: [
          "Cell cycle phases: $G_1$ (growth), $S$ phase (DNA replication amount $2C \to 4C$, chromosome number stays $2n$), $G_2$ (protein synthesis), $G_0$ (quiescent stage of non-dividing cells)",
          "Mitosis stages: Prophase (chromatin condensation), Metaphase (chromosomes align at equatorial plate, spindle fibers attach to kinetochores), Anaphase (centromeres split, sister chromatids separate), Telophase, Cytokinesis",
          "Meiosis I reductional division: Prophase I substages (Leptotene, Zygotene synapsis and synaptonemal complex, Pachytene crossing over via recombinase enzyme, Diplotene chiasmata appearance, Diakinesis terminalisation)",
          "Meiosis II equational division and significance of meiosis in genetic variation"
        ],
        commonMistakeWarning: "S-phase me DNA content double ($2C \to 4C$) ho jata hai par chromosome count same ($2n$) rehta hai; chromosome count Anaphase I me nahi, Telophase I ke baad reduce hota hai."
      },
      {
        id: "bio-11-11",
        chapter: "Photosynthesis in Higher Plants",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Cell: The Unit of Life (Chloroplast)", "Biomolecules (Enzymes)"],
        coreSkills: [
          "Chloroplast pigments: Chlorophyll a (chief pigment), Chlorophyll b, Carotenoids, Xanthophylls; Absorption and Action spectrum",
          "Light reaction (Photochemical phase): Photosystem II ($P_{680}$) splitting of water ($2H_2O \to 4H^+ + O_2 + 4e^-$), Non-cyclic photophosphorylation ($Z$-scheme), Photosystem I ($P_{700}$), Cyclic photophosphorylation, Chemiosmotic ATP synthesis via proton gradient ($F_0-F_1$ ATP synthase)",
          "Dark reaction (Biosynthetic phase): $C_3$ Calvin Cycle (Carboxylation by RuBisCO, Reduction, Regeneration; requires $3\text{ ATP} + 2\text{ NADPH}$ per $CO_2$)",
          "$C_4$ Pathway (Hatch-Slack): Kranz anatomy, Mesophyll cells (PEP carboxylase fixes $CO_2$ into $OAA$) $\to$ Bundle sheath cells (RuBisCO, Decarboxylation); avoids photorespiration",
          "Photorespiration ($C_2$ cycle in chloroplast, peroxisome, mitochondria when RuBisCO binds $O_2$), and Blackman's Law of Limiting Factors"
        ],
        commonMistakeWarning: "$C_4$ plants me photorespiration absent hoti hai kyunki bundle sheath cells me $CO_2$ concentration high maintain rehti hai; inki productivity aur temperature tolerance $C_3$ se higher hoti hai."
      },
      {
        id: "bio-11-12",
        chapter: "Respiration in Plants",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Cell: The Unit of Life (Mitochondria)", "Biomolecules"],
        coreSkills: [
          "Glycolysis (EMP Pathway in cytoplasm): 10-step anaerobic breakdown of Glucose $\to 2$ Pyruvate; Net gain $= 2\text{ ATP} + 2\text{ NADH}$",
          "Fermentation: Alcoholic (Pyruvate decarboxylase + Alcohol dehydrogenase) and Lactic acid fermentation; Net gain $= 2\text{ ATP}$",
          "Aerobic Link Reaction (Pyruvate dehydrogenase in mitochondrial matrix $\to \text{Acetyl-CoA} + CO_2 + \text{NADH}$)",
          "Krebs Cycle / TCA Cycle in matrix: Citrate synthase, Isocitrate $\to \alpha$-ketoglutarate $\to$ Succinyl-CoA (GTP generated) $\to$ Succinate $\to$ Fumarate ($FADH_2$) $\to$ Malate $\to$ OAA",
          "Electron Transport System (ETS) & Oxidative Phosphorylation on inner mitochondrial membrane: Complexes I, II, III, IV (Cytochrome c oxidase), Complex V (ATP synthase), and Respiratory Quotient ($RQ = \frac{\text{Volume of } CO_2 \text{ evolved}}{\text{Volume of } O_2 \text{ consumed}}$; Carbohydrates $=1$, Fats $=0.7$, Proteins $=0.9$)"
        ],
        commonMistakeWarning: "Terminal electron acceptor aerobic respiration me molecular oxygen ($O_2$) hota hai jo $H^+$ ke sath react karke metabolic water banata hai."
      },
      {
        id: "bio-11-13",
        chapter: "Plant Growth and Development",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Plant Physiology Basics"],
        coreSkills: [
          "Phases of growth: Meristematic, Elongation, Maturation; Arithmetic growth ($L_t = L_0 + rt$) vs Geometric growth ($W_1 = W_0 e^{rt}$ sigmoid curve)",
          "Differentiation, Dedifferentiation (formation of interfascicular cambium from parenchyma), Redifferentiation (secondary xylem/phloem formation)",
          "Plasticity in plants (Heterophylly in Cotton, Coriander, Larkspur vs Buttercup in water)",
          "Plant Growth Regulators (PGRs): Auxins (apical dominance, rooting, 2,4-D herbicide), Gibberellins (bolting, internode elongation in sugarcane), Cytokinins (cell division, delay senescence), Ethylene (fruit ripening, epinasty), Abscisic Acid (stress hormone, stomatal closure, seed dormancy)"
        ],
        commonMistakeWarning: "Auxin apical bud me synthesize hoke lateral buds ki growth inhibit karta hai (apical dominance); apical bud cut karne par Cytokinin lateral shoot growth promote karta hai."
      },
      {
        id: "bio-11-14",
        chapter: "Breathing and Exchange of Gases",
        classLevel: "11th",
        difficultyRating: "Low",
        mandatoryChapters: ["Animal Tissues", "Basic Physics of Pressure/Gradients"],
        coreSkills: [
          "Human respiratory system anatomy: Pharynx $\to$ Larynx $\to$ Trachea $\to$ Primary/Secondary/Tertiary Bronchi $\to$ Bronchioles $\to$ Alveoli",
          "Mechanism of breathing: Inspiration (Diaphragm & External intercostal muscles contract) vs Expiration (muscles relax)",
          "Respiratory volumes & capacities: Tidal Volume ($500\text{ mL}$), IRV ($2500-3000\text{ mL}$), ERV ($1000-1100\text{ mL}$), Residual Volume ($1100-1200\text{ mL}$), Vital Capacity ($VC = ERV+TV+IRV$), Total Lung Capacity ($TLC = VC + RV$)",
          "Gas exchange across alveolar membrane (Thickness $< 1\text{ mm}$, 3 layers: squamous epithelium, basement substance, capillary endothelium); Partial pressures $pO_2, pCO_2$",
          "Oxygen transport: Oxygen-Hemoglobin dissociation curve (Sigmoid curve; shifts right with high $pCO_2$, high $H^+$/low pH, high temperature, Bohr effect); $CO_2$ transport as Bicarbonate ($70\%$), Carbamino-hemoglobin ($20-25\%$), Dissolved in plasma ($7\%$)"
        ],
        commonMistakeWarning: "Respiratory rhythm center medulla oblongata me hota hai aur pneumotaxic center pons me hota hai; inka regulation primarily $CO_2$ aur $H^+$ ion concentration se hota hai, oxygen concentration se nahi."
      },
      {
        id: "bio-11-15",
        chapter: "Body Fluids and Circulation",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Animal Tissues (Blood & Muscle)"],
        coreSkills: [
          "Blood composition: Plasma proteins (Fibrinogen clotting, Globulins immunity, Albumin osmotic balance), Formed elements (RBCs, WBCs: Neutrophils, Basophils histamine/heparin, Eosinophils, Monocytes, Lymphocytes B & T, Platelets)",
          "ABO Blood grouping and Rh factor incompatibility (Erythroblastosis fetalis in Rh-ve mother carrying Rh+ve fetus)",
          "Blood coagulation cascade: Thromboplastin $\to$ Prothrombinase $\to$ Thrombin $\to$ Fibrin polymer mesh; role of $Ca^{2+}$ ions",
          "Human heart anatomy and Cardiac cycle: SA Node (Pacemaker $72\text{ bpm}$) $\to$ AV Node $\to$ Bundle of His $\to$ Purkinje fibers; Stroke volume ($70\text{ mL}$), Cardiac Output ($CO = SV \times HR \approx 5\text{ L/min}$)",
          "Electrocardiogram (ECG): P-wave (Atrial depolarization), QRS complex (Ventricular depolarization), T-wave (Ventricular repolarization); Double circulation (Systemic & Pulmonary), Hepatic portal system"
        ],
        commonMistakeWarning: "QRS complex ventricular depolarization ko represent karta hai; ECG me QRS complexes count karke patient ki heart rate determine ki jati hai."
      },
      {
        id: "bio-11-16",
        chapter: "Excretory Products and Their Elimination",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Animal Tissues", "Body Fluids and Circulation"],
        coreSkills: [
          "Modes of excretion: Ammonotelic (bony fishes), Ureotelic (mammals, amphibians), Uricotelic (birds, reptiles, insects - minimum water loss)",
          "Human excretory system: Kidney internal structure (Cortex, Medulla with renal pyramids), Nephron anatomy (Cortical vs Juxtamedullary nephrons with long Loop of Henle)",
          "Urine formation steps: Glomerular filtration (GFR $\approx 125\text{ mL/min}$ or $180\text{ L/day}$), Tubular reabsorption ($99\%$ reabsorbed in PCT - all glucose, amino acids, $70-80\%$ electrolytes), Tubular secretion ($H^+, K^+, NH_3$ in PCT/DCT)",
          "Counter-Current Mechanism in Loop of Henle and Vasa Recta for creating hyperosmolar medullary interstitium ($300 \to 1200\text{ mOsmol/L}$)",
          "Regulation of kidney function: Renin-Angiotensin-Aldosterone System (RAAS by JGA), Antidiuretic Hormone (ADH / Vasopressin from posterior pituitary), Atrial Natriuretic Factor (ANF from heart - vasodilator check on RAAS)"
        ],
        commonMistakeWarning: "Descending limb of Loop of Henle water ke liye permeable hoti hai par electrolytes ke liye impermeable; Ascending limb electrolytes ke liye permeable hoti hai par water ke liye impermeable."
      },
      {
        id: "bio-11-17",
        chapter: "Locomotion and Movement",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Animal Tissues (Muscle & Skeletal Tissue)"],
        coreSkills: [
          "Types of movement: Amoeboid (macrophages), Ciliary (trachea, oviduct), Muscular",
          "Structure of skeletal muscle: Fascia, Muscle fascicles, Muscle fiber, Myofibrils; Sarcomere structure (Z-line, I-band thin actin, A-band thick myosin, H-zone center of thick filaments without actin, M-line)",
          "Contractile proteins: Actin (F-actin polymer of G-actin, Tropomyosin, Troponin binding $Ca^{2+}$) and Myosin (Meromyosin: Heavy Meromyosin head with ATPase activity, Light Meromyosin tail)",
          "Sliding Filament Theory of muscle contraction: Neuromuscular junction $\to$ Action potential $\to$ Sarcoplasmic reticulum releases $Ca^{2+} \to Ca^{2+}$ binds Troponin C $\to$ Unmasks active sites on actin $\to$ Myosin head binds actin $\to$ Power stroke $\to$ ATP binds myosin head $\to$ Cross-bridge detachment",
          "Human skeletal system: 206 bones (Axial 80: Skull, Vertebral column 26, Ribs 12 pairs - 7 true, 3 false, 2 floating, Sternum; Appendicular 126: Pectoral girdle, Pelvic girdle, Limbs)",
          "Joints: Fibrous (immovable sutures in skull), Cartilaginous (limited movement in vertebrae), Synovial (freely movable: Ball & socket, Hinge, Pivot, Gliding, Saddle); Disorders: Myasthenia gravis, Tetany, Muscular dystrophy, Arthritis, Osteoporosis, Gout"
        ],
        commonMistakeWarning: "Muscle contraction ke dauran A-band ki length constant rehti hai; I-band shorten hota hai, H-zone disappear ho jati hai aur Z-lines paas aati hain."
      },
      {
        id: "bio-11-18",
        chapter: "Neural Control and Coordination",
        classLevel: "11th",
        difficultyRating: "High",
        mandatoryChapters: ["Animal Tissues (Nervous Tissue)", "Cell: The Unit of Life (Membrane Transport)"],
        coreSkills: [
          "Neuron structure: Dendrites, Cell body (Nissl's granules), Axon, Myelin sheath (Schwann cells in PNS, Oligodendrocytes in CNS, Nodes of Ranvier)",
          "Generation and conduction of nerve impulse: Resting potential ($-70\text{ mV}$, high $K^+$ inside, high $Na^+$ outside maintained by $3 Na^+ \text{ out} / 2 K^+ \text{ in}$ ATPase pump), Depolarization ($Na^+$ influx through voltage-gated channels $\to +30\text{ mV}$), Repolarization ($K^+$ efflux), Refractory period",
          "Synaptic transmission: Electrical synapses (gap junctions, faster) vs Chemical synapses (Synaptic cleft, neurotransmitters like Acetylcholine released from synaptic vesicles via $Ca^{2+}$ influx $\to$ bind post-synaptic receptors)",
          "Human brain division: Forebrain (Cerebrum corpus callosum, Thalamus, Hypothalamus temperature/hunger/hormone regulation, Limbic system emotions/memory), Midbrain (Corpora quadrigemina), Hindbrain (Pons, Cerebellum balance/coordination, Medulla oblongata respiration/cardiovascular/vomiting reflexes)"
        ],
        commonMistakeWarning: "Resting membrane potential par membrane $K^+$ ions ke liye significantly zyada permeable hoti hai than $Na^+$ ions; isliye resting potential negative inside rehta hai."
      },
      {
        id: "bio-11-19",
        chapter: "Chemical Coordination and Integration",
        classLevel: "11th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Neural Control and Coordination", "Biomolecules"],
        coreSkills: [
          "Endocrine glands vs Exocrine glands; Hormone characteristics (non-nutrient intercellular messengers in trace amounts)",
          "Hypothalamus hormones (Releasing and Inhibiting hormones) and Pituitary gland (Anterior: GH, PRL, TSH, ACTH, LH, FSH; Intermediate: MSH; Posterior/Neurohypophysis stores Oxytocin and Vasopressin/ADH produced by hypothalamus)",
          "Thyroid gland ($T_3, T_4$, Thyrocalcitonin decreases blood $Ca^{2+}$; Goitre, Cretinism), Parathyroid gland (PTH increases blood $Ca^{2+}$ by bone resorption - antagonistic to TCT)",
          "Adrenal gland: Cortex (Mineralocorticoids/Aldosterone, Glucocorticoids/Cortisol, Androgenic steroids) and Medulla (Catecholamines: Adrenaline/Epinephrine and Noradrenaline - emergency fight/flight hormones)",
          "Pancreas (Islets of Langerhans: $\alpha$-cells Glucagon hyperglycemic, $\beta$-cells Insulin hypoglycemic), Gonads (Testes: Testosterone from Leydig cells; Ovaries: Estrogen from follicles, Progesterone from corpus luteum)",
          "Hormone mechanism of action: Water-soluble peptide hormones bind membrane receptors $\to$ generate second messengers ($cAMP, IP_3, Ca^{2+}$); Lipid-soluble steroid/thyroid hormones enter cell $\to$ bind intracellular nuclear receptors $\to$ alter gene expression"
        ],
        commonMistakeWarning: "Insulin blood glucose level ko kam karta hai (cellular glucose uptake badha ke); Glucagon blood glucose level ko badhata hai (glycogenolysis promote karke)."
      },

      // --- CLASS 12 BOTANY & ZOOLOGY ---
      {
        id: "bio-12-01",
        chapter: "Sexual Reproduction in Flowering Plants",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Morphology of Flowering Plants", "Cell Division (Meiosis)"],
        coreSkills: [
          "Male reproductive unit (Stamen): Microsporangium structure (Epidermis, Endothecium, Middle layers, Tapetum binucleate/nourishing), Microsporogenesis $\to$ Pollen grain (Vegetative cell larger, Generative cell divides into 2 male gametes; Exine made of sporopollenin, Intine cellulose/pectin)",
          "Female reproductive unit (Pistil): Megasporangium (Ovule: Funicle, Integuments, Nucellus, Micropyle, Chalaza), Megasporogenesis $\to$ Functional megaspore undergoes 3 mitotic divisions to form 7-celled, 8-nucleate Embryo sac (3 Antipodals, 2 Polar nuclei in central cell, 1 Egg cell + 2 Synergids with filiform apparatus)",
          "Pollination: Autogamy, Geitonogamy (functionally cross, genetically self), Xenogamy (true cross-pollination); Outbreeding devices to prevent inbreeding; Agents of pollination (Wind, Water, Insects entomophily)",
          "Double Fertilization (unique to Angiosperms): Syngamy (Male gamete + Egg $\to$ Diploid Zygote $2n$) + Triple Fusion (Male gamete + 2 Polar nuclei $\to$ Triploid Primary Endosperm Nucleus $PEN$ $3n$)",
          "Post-fertilization events: Endosperm development (Free-nuclear in coconut water vs Cellular), Embryo development (Dicot with 2 cotyledons vs Monocot with Scutellum, Coleoptile, Coleorhiza), Seed structure, Perisperm (persistent nucellus in Black pepper, Beet), Apomixis (asexual seed formation without fertilization) and Polyembryony"
        ],
        commonMistakeWarning: "Sporopollenin ab tak ka most resistant organic material hai jo kisi bhi known enzyme, high temperature, strong acid ya alkali se degrade nahi hota; isliye fossils me pollen grains well-preserved rehte hain."
      },
      {
        id: "bio-12-02",
        chapter: "Human Reproduction",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Cell Division (Meiosis)", "Chemical Coordination (Hormones)"],
        coreSkills: [
          "Male Reproductive System: Testes in scrotum (temperature $2-2.5^\circ\text{C}$ lower for spermatogenesis), Seminiferous tubules (Sertoli cells nourish, Leydig cells secrete testosterone), Accessory ducts (Rete testis, Vasa efferentia, Epididymis, Vas deferens), Accessory glands (Seminal vesicles, Prostate gland, Bulbourethral glands)",
          "Female Reproductive System: Ovaries, Fallopian tubes (Infundibulum with fimbriae, Ampulla site of fertilization, Isthmus), Uterus (Perimetrium, Myometrium smooth muscle, Endometrium cyclic changes), Vagina, Mammary glands",
          "Gametogenesis: Spermatogenesis (Spermatogonia $2n \to$ Primary spermatocytes $2n \to$ Meiosis I $\to$ Secondary spermatocytes $n \to$ Meiosis II $\to$ Spermatids $n \to$ Spermiogenesis $\to$ Spermatozoa) vs Oogenesis (Oogonia $2n \to$ Primary oocytes arrested at Prophase I $\to$ Puberty completes Meiosis I $\to$ Secondary oocyte $n$ arrested at Metaphase II + 1st polar body; Meiosis II completes only upon sperm entry)",
          "Menstrual Cycle (28 days): Menstrual phase ($1-5$ days) $\to$ Follicular/Proliferative phase ($6-13$ days, Estrogen rises) $\to$ Ovulatory phase (Day 14, LH Surge triggers ovulation) $\to$ Luteal/Secretory phase ($15-28$ days, Corpus luteum secretes Progesterone)",
          "Fertilization (Acrosome reaction penetrates Corona radiata and Zona pellucida, block to polyspermy) $\to$ Cleavage $\to$ Morula $\to$ Blastocyst (Trophoblast attaches endometrium, Inner cell mass forms embryo) $\to$ Implantation $\to$ Placenta hormones (hCG, hPL, Estrogen, Progesterone, Relaxin) $\to$ Parturition (Fetal ejection reflex, Oxytocin positive feedback) $\to$ Lactation (Prolactin milk synthesis, Oxytocin milk letdown, Colostrum rich in IgA antibodies)"
        ],
        commonMistakeWarning: "Secondary oocyte me Meiosis II tab tak complete nahi hoti jab tak sperm ki entry na ho jaye; sperm entry ke baad hi 2nd polar body release hoti hai aur ovum nucleus banta hai."
      },
      {
        id: "bio-12-03",
        chapter: "Reproductive Health",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Human Reproduction"],
        coreSkills: [
          "Problems and strategies: Family planning programs (RCH), Amniocentesis (sex determination misuse vs chromosomal abnormality detection)",
          "Contraceptive methods: Natural/Traditional (Periodic abstinence, Coitus interruptus, Lactational amenorrhea up to 6 months), Barrier methods (Condoms prevent STIs, Diaphragms, Cervical caps), Intrauterine Devices / IUDs (Non-medicated: Lippes loop; Copper-releasing: CuT, Cu7, Multiload 375 suppress sperm motility; Hormone-releasing: Progestasert, LNG-20 make uterus unsuitable for implantation), Oral pills (Saheli non-steroidal once a week), Injectables/Implants, Surgical sterilization (Vasectomy in males, Tubectomy in females)",
          "Medical Termination of Pregnancy (MTP Act safe up to 12 weeks of first trimester), Sexually Transmitted Infections (STIs: Gonorrhea, Syphilis, Genital herpes, Chlamydiasis, Hepatitis-B, HIV incurable)",
          "Infertility and Assisted Reproductive Technologies (ART): In Vitro Fertilization / IVF (Test tube baby: ZIFT transfer up to 8 blastomeres into fallopian tube, IUT transfer $>8$ blastomeres into uterus), Gamete Intra-Fallopian Transfer (GIFT), Intra-Cytoplasmic Sperm Injection (ICSI), Artificial Insemination (IUI)"
        ],
        commonMistakeWarning: "ZIFT ($Zygote$ or embryo up to 8 blastomeres) Fallopian tube me transfer hota hai, jabki IUT (embryo with more than 8 blastomeres) direct Uterus me transfer hota hai."
      },
      {
        id: "bio-12-04",
        chapter: "Principles of Inheritance and Variation (Genetics 1)",
        classLevel: "12th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Cell Division (Meiosis)", "Basic Probability"],
        coreSkills: [
          "Mendel's Laws: Monohybrid cross (Law of Dominance and Law of Segregation, phenotypic ratio $3:1$, genotypic ratio $1:2:1$), Dihybrid cross (Law of Independent Assortment, ratio $9:3:3:1$), Test cross (Dominant phenotype $\times$ Homozygous recessive)",
          "Non-Mendelian inheritance: Incomplete Dominance (Snapdragon / Antirrhinum majus red/pink/white $1:2:1$), Co-dominance (ABO blood grouping with $I^A, I^B, i$ alleles), Multiple alleles, Pleiotropy (single gene multiple traits, Phenylketonuria), Polygenic inheritance (Human skin color Gaussian distribution)",
          "Chromosomal Theory of Inheritance by Sutton & Boveri; Morgan's experimental verification with Drosophila melanogaster; Linkage and Recombination (frequency proportional to distance between genes on chromosome, Alfred Sturtevant gene mapping)",
          "Sex determination: XX-XY (Humans, Drosophila), XX-XO (Grasshopper), ZZ-ZW (Birds - female heterogamety), Haplodiploidy in Honeybees",
          "Mutation and Genetic Disorders: Pedigree analysis symbols and rules (Autosomal dominant: Myotonic dystrophy; Autosomal recessive: Sickle-cell anemia $Hb^A \to Hb^S$ GAG to GUG point mutation, Phenylketonuria, Thalassemia; Sex-linked recessive: Hemophilia, Color blindness); Chromosomal abnormalities (Aneuploidy: Down's syndrome Trisomy 21, Turner's syndrome 45 XO, Klinefelter's syndrome 47 XXY)"
        ],
        commonMistakeWarning: "Independent assortment un genes ke liye apply nahi hota jo same chromosome par tightly linked hote hain; linked genes me recombinant progeny ka percentage parental type se significantly kam hota hai."
      },
      {
        id: "bio-12-05",
        chapter: "Molecular Basis of Inheritance (Genetics 2)",
        classLevel: "12th",
        difficultyRating: "Very High",
        mandatoryChapters: ["Principles of Inheritance", "Biomolecules (Nucleic Acids)"],
        coreSkills: [
          "DNA as genetic material evidence: Griffith's Transformation experiment (Streptococcus pneumoniae $S$-strain vs $R$-strain), Avery-MacLeod-McCarty biochemical characterization (DNase destroyed transforming principle), Hershey-Chase bacteriophage experiment ($^{35}S$ protein vs $^{32}P$ DNA)",
          "DNA Structure: Purines (A, G), Pyrimidines (C, T, U), Chargaff's rules ($A+G = C+T$, $A/T = G/C = 1$), Packaging of DNA helix in eukaryotes (Histone octamer, Nucleosome 200 bp, Euchromatin active/light vs Heterochromatin inactive/dark)",
          "DNA Replication: Semiconservative replication proof by Meselson & Stahl using $^{15}N$ and $^{14}N$ density gradient centrifugation in E. coli; Replication machinery (Helicase, Topoisomerase, Primase, DNA Polymerase III reads $3' \to 5'$ synthesizes $5' \to 3'$, Leading strand continuous vs Lagging strand Okazaki fragments joined by DNA Ligase)",
          "Transcription: Transcription unit (Promoter, Structural gene, Terminator); Prokaryotes (single RNA polymerase, Polycistronic mRNA) vs Eukaryotes (Monocistronic, RNA Pol I rRNAs, RNA Pol II hnRNA/mRNA, RNA Pol III tRNAs/5S rRNA/snRNA; Post-transcriptional processing: Capping 7-methylguanosine, Tailing poly-A tail, Splicing removes introns joins exons via spliceosome)",
          "Genetic Code: 64 codons, Triplet, Degenerate (multiple codons one amino acid), Unambiguous, Universal, Non-overlapping, Start codon AUG (Methionine), Stop codons UAA, UAG, UGA; tRNA adapter molecule (Cloverleaf 2D model, L-shaped 3D, anticodon loop, amino acid acceptor arm)",
          "Translation: Aminoacylation of tRNA (charging) $\to$ Ribosome binding mRNA $\to$ Initiation $\to$ Elongation (Peptidyl transferase 23S rRNA ribozyme in prokaryotes) $\to$ Termination via Release factor",
          "Regulation of Gene Expression: Operon concept by Jacob & Monod; Lac Operon in E. coli (i-gene produces repressor protein $\to$ in absence of lactose binds operator gene $o \to$ switches off operon; Lactose / Allolactose acts as inducer $\to$ binds repressor $\to$ enables RNA polymerase to transcribe structural genes $z$ for $\beta$-galactosidase, $y$ for permease, $a$ for transacetylase); Human Genome Project (HGP) salient features and DNA Fingerprinting (VNTRs satellite DNA minisatellites, Southern blotting, autoradiography)"
        ],
        commonMistakeWarning: "DNA polymerase aur RNA polymerase dono hamesha naya strand sirf $5' \to 3'$ direction me synthesize karte hain, iska matlab template strand ko $3' \to 5'$ direction me read kiya jata hai."
      },
      {
        id: "bio-12-06",
        chapter: "Evolution",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Molecular Basis of Inheritance", "Animal Kingdom"],
        coreSkills: [
          "Origin of Life: Big Bang Theory, Oparin-Haldane chemical evolution hypothesis (reducing primitive atmosphere of $CH_4, NH_3, H_2, H_2O$ vapor), Miller-Urey experiment (electric discharge formed amino acids: glycine, alanine, aspartic acid)",
          "Evidences for evolution: Paleontological (fossils), Comparative anatomy (Homologous organs divergent evolution e.g. vertebrate forelimbs, thorn of Bougainvillea & tendril of Cucurbita; Analogous organs convergent evolution e.g. wings of bird & butterfly, eye of octopus & mammal, flippers of penguin & dolphin, sweet potato root & potato stem), Embryological (Biogenetic law rejected by Ernst von Baer), Industrial Melanism in peppered moth (Biston betularia natural selection)",
          "Adaptive Radiation: Darwin's Finches in Galapagos Islands, Australian Marsupials vs Placental Mammals convergent evolution",
          "Biological Evolution Theories: Lamarckism (use and disuse of organs), Darwinism (Survival of the fittest, Natural selection on variations), Hugo de Vries Mutation Theory (Mutations are random, directionless, cause saltation single-step large mutation)",
          "Hardy-Weinberg Principle: Allele frequencies in a stable population remain constant ($p + q = 1, p^2 + 2pq + q^2 = 1$); Factors affecting equilibrium: Gene flow, Genetic drift (Founder effect, Bottleneck effect in small populations), Mutation, Genetic recombination, Natural selection (Stabilizing, Directional, Disruptive selection graphs)",
          "Human Evolution chronological order: Dryopithecus/Ramapithecus $\to$ Australopithecus (fruit eater) $\to$ Homo habilis ($650-800\text{ cc}$, first human-like, didn't eat meat) $\to$ Homo erectus ($900\text{ cc}$, Java man, ate meat) $\to$ Neanderthal man ($1400\text{ cc}$, buried dead, cave dweller) $\to$ Homo sapiens modern human ($1350\text{ cc}$)"
        ],
        commonMistakeWarning: "Homology common ancestry (divergent evolution) ko represent karti hai chahe functions different hon; Analogy similar function under common selection pressure (convergent evolution) ko represent karti hai chahe anatomy different ho."
      },
      {
        id: "bio-12-07",
        chapter: "Human Health and Disease",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Cell: The Unit of Life", "Body Fluids & Circulation"],
        coreSkills: [
          "Common human pathogens: Typhoid (Salmonella typhi, Widal test), Pneumonia (Streptococcus pneumoniae, alveoli fluid filled), Common cold (Rhino virus infects nose not lungs), Malaria (Plasmodium vivax/falciparum life cycle: female Anopheles mosquito vector transmits sporozoites $\to$ liver schizogony $\to$ RBC rupture releases toxic hemozoin causing chills/fever $\to$ gametocytes formed in human blood $\to$ sexual fertilization in mosquito gut $\to$ sporozoites stored in salivary gland), Amoebiasis (Entamoeba histolytica), Ascariasis (Ascaris lumbricoides), Elephantiasis/Filariasis (Wuchereria bancrofti in lymphatic vessels of lower limbs), Ringworm (Microsporum, Trichophyton, Epidermophyton)",
          "Immunity types: Innate immunity (Physical barriers skin/mucus, Physiological tears/acid, Cellular phagocytes/macrophages/NK cells, Cytokine interferons protect non-infected cells from virus) vs Acquired immunity (Active natural infection/vaccine vs Passive readymade antibodies e.g. Colostrum IgA, anti-tetanus serum, anti-venom)",
          "Immune system components: Humoral immune response (B-lymphocytes produce antibodies: $IgG$ most abundant crosses placenta, $IgA$ secretory, $IgM$ pentamer first produced, $IgE$ allergy histamine release, $IgD$; structure $H_2L_2$ with disulfide bonds) vs Cell-Mediated Immunity / CMI (T-lymphocytes responsible for graft rejection in organ transplants)",
          "Autoimmunity (Rheumatoid arthritis, Myasthenia gravis self vs non-self breakdown), Allergy (exaggerated response to allergens, IgE, mast cells release histamine/serotonin), Primary lymphoid organs (Bone marrow, Thymus maturation sites) vs Secondary lymphoid organs (Spleen, Lymph nodes, Tonsils, MALT 50% of lymphoid tissue)",
          "AIDS (Acquired Immuno Deficiency Syndrome): Caused by HIV (Retrovirus with single-stranded RNA and reverse transcriptase enzyme); Mode of action: HIV infects Macrophages (HIV factory) $\to$ reverse transcription forms viral DNA $\to$ integrates into host genome $\to$ infects Helper T-lymphocytes ($CD4^+$ count drops $<200/\mu\text{L}$) $\to$ opportunistic infections (Mycobacterium, Toxoplasma); Diagnostic test ELISA, confirmation Western Blot",
          "Cancer: Benign vs Malignant tumors (Metastasis property of cancer cells spreading to distant sites), Properties (loss of contact inhibition, immortal), Causes (Carcinogens: physical X-rays/UV, chemical tobacco, oncogenic viruses), Detection (Biopsy, MRI, CT scan), Treatment (Surgery, Radiotherapy, Chemotherapy, Immunotherapy $\alpha$-interferon)",
          "Drugs and Alcohol Abuse: Opioids (Heroin/Smack extracted from Papaver somniferum poppy latex, depressant, binds opioid receptors in CNS/GI tract), Cannabinoids (from Cannabis sativa, affects cardiovascular system, marijuana/hashish/charas/ganja), Cocaine/Coca alkaloid (from Erythroxylum coca, interferes with dopamine transport, causes euphoria/hallucinations), Tobacco (Nicotine stimulates adrenal gland to release adrenaline raising blood pressure)"
        ],
        commonMistakeWarning: "Organ transplant rejection ke liye Cell-Mediated Immunity (T-lymphocytes) responsible hoti hai, Humoral immunity nahi; isliye transplant patients ko lifelong immunosuppressants (jaise Cyclosporin A) lene padte hain."
      },
      {
        id: "bio-12-08",
        chapter: "Microbes in Human Welfare",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Biological Classification", "Human Health and Disease"],
        coreSkills: [
          "Microbes in household products: LAB (Lactic Acid Bacteria converts milk to curd, improves nutritional quality by increasing Vitamin $B_{12}$), Baker's Yeast (Saccharomyces cerevisiae fermentation releases $CO_2$ puffing dough), Swiss cheese large holes due to $CO_2$ by Propionibacterium shermanii, Roquefort cheese ripened by Penicillium roqueforti, Toddy fermented palm sap",
          "Microbes in industrial products: Fermented beverages (Brewer's yeast), Antibiotics (Penicillin discovered by Alexander Fleming from Penicillium notatum, full potential shown by Florey & Chain), Organic acids (Citric acid by Aspergillus niger fungus, Acetic acid by Acetobacter aceti, Butyric acid by Clostridium butyricum, Lactic acid by Lactobacillus), Enzymes (Lipases in detergents, Pectinases/Proteases for clarifying bottled fruit juices, Streptokinase clot buster from Streptococcus for myocardial infarction patients), Bioactive molecules (Cyclosporin A immunosuppressive agent from Trichoderma polysporum fungus, Statins blood cholesterol lowering agent from Monascus purpureus yeast competitively inhibiting HMG-CoA reductase)",
          "Microbes in Sewage Treatment: Primary treatment (physical filtration and sequential sedimentation) $\to$ Secondary / Biological treatment (aerobic aeration tanks where aerobic microbes form 'flocs' $\to$ consume organic matter, reducing Biochemical Oxygen Demand / BOD $\to$ settling tank forms activated sludge $\to$ part pumped to Anaerobic Sludge Digesters where anaerobic bacteria produce Biogas: $CH_4, H_2S, CO_2$)",
          "Biogas production: Methanogens (Methanobacterium) in anaerobic sludge and rumen of cattle (gobar gas plant technology by IARI and KVIC)",
          "Microbes as Biocontrol Agents: Bacillus thuringiensis (Bt toxin against butterfly caterpillars), Trichoderma fungus (free-living in root ecosystems against plant pathogens), Baculoviruses (genus Nucleopolyhedrovirus: species-specific, narrow spectrum insecticidal applications safe for non-target organisms)",
          "Microbes as Biofertilizers: Rhizobium symbiotic in leguminous root nodules, Free-living nitrogen fixers (Azotobacter, Azospirillum), Mycorrhiza (fungus Glomus absorbs phosphorus for plant), Cyanobacteria (Anabaena, Nostoc, Oscillatoria in paddy fields)"
        ],
        commonMistakeWarning: "BOD (Biochemical Oxygen Demand) organic matter ke pollution level ke directly proportional hoti hai; jitna zyada BOD hoga, water utna zyada polluted hoga."
      },
      {
        id: "bio-12-09",
        chapter: "Biotechnology: Principles and Processes",
        classLevel: "12th",
        difficultyRating: "High",
        mandatoryChapters: ["Molecular Basis of Inheritance", "Cell Biology"],
        coreSkills: [
          "Core principles of Biotechnology: Genetic Engineering (recombinant DNA technology) and Bioprocess engineering (sterile ambience for product manufacture); Father of Genetic Engineering: Paul Berg",
          "Tools of Recombinant DNA Technology: Restriction Endonucleases ('molecular scissors' cut DNA at specific palindromic recognition sequences, leave sticky ends e.g. EcoRI from Escherichia coli RY13 cuts $5'-G^{\downarrow}AATTC-3'$); DNA Ligase ('molecular glue'); DNA Polymerase",
          "Gel Electrophoresis: Agarose gel derived from seaweeds, separates DNA fragments based on size/charge (DNA is negatively charged so moves towards positive anode), smaller fragments move farthest; Visualization requires staining with Ethidium Bromide (EtBr) under UV light (bright orange bands), Elution (cutting bands from gel)",
          "Cloning Vectors: Characteristics (Origin of replication / $ori$ controls copy number, Selectable markers like antibiotic resistance genes $amp^R, tet^R$ to distinguish transformants from non-transformants, Unique restriction / cloning sites); pBR322 vector map; Insertional Inactivation (e.g. inserting gene into $tet^R$ makes recombinant tetracycline sensitive, or inserting into $\beta$-galactosidase gene causes blue-white screening where white colonies are recombinants); Vectors for plants (Agrobacterium tumefaciens Ti-plasmid) and animals (Disarmed Retrovirus)",
          "Competent host cell preparation: Chemical method (divalent cation $Ca^{2+}$ followed by heat shock $42^\circ\text{C}$), Microinjection (direct injection into animal cell nucleus), Biolistics / Gene gun (gold or tungsten microparticles coated with DNA for plant cells)",
          "Processes of rDNA technology: Isolation of DNA (Lysozyme for bacteria, Cellulase for plants, Chitinase for fungi; RNAse and Protease treatment $\to$ precipitate DNA with chilled ethanol) $\to$ Cutting DNA $\to$ PCR (Polymerase Chain Reaction: Denaturation $94^\circ\text{C}$, Primer Annealing $54^\circ\text{C}$, Primer Extension $72^\circ\text{C}$ using thermostable Taq Polymerase from Thermus aquaticus; $10^9$ copies amplified) $\to$ Insertion into host $\to$ Culturing in Bioreactors (Stirred-tank bioreactor with sparger for continuous oxygen) $\to$ Downstream Processing (Separation, purification, quality testing, and preservation)"
        ],
        commonMistakeWarning: "Insertional inactivation of $\beta$-galactosidase me agar recombinant DNA successfully insert ho jata hai to colonies white rehti hain (no color), jabki non-recombinant colonies enzyme active hone ki wajah se blue color deti hain."
      },
      {
        id: "bio-12-10",
        chapter: "Biotechnology and Its Applications",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Biotechnology: Principles and Processes"],
        coreSkills: [
          "Applications in Agriculture: Bt Crops (Bt Cotton produced using endotoxin genes from Bacillus thuringiensis: $cryIAc$ and $cryIIAb$ control cotton bollworms, $cryIAb$ controls corn borer; inactive protoxin converts to active toxin in alkaline pH of insect gut, creates pores in midgut epithelial cells causing lysis and death); Pest-resistant plants using RNA Interference / RNAi (in Tobacco against nematode Meloidogyne incognita: Agrobacterium vector introduces both sense and anti-sense RNA $\to$ forms dsRNA $\to$ silences specific nematode mRNA by RISC complex); Golden Rice (enriched with Vitamin A / $\beta$-carotene)",
          "Applications in Medicine: Genetically Engineered Human Insulin (Humulin by Eli Lilly in 1983: synthesized separate A and B peptide chains in E. coli, linked by disulfide bonds; eliminates C-peptide present in proinsulin); Gene Therapy (first clinical gene therapy in 1990 for 4-year-old girl with Adenosine Deaminase / ADA deficiency using retroviral vector into patient's lymphocytes); Molecular Diagnosis: PCR (detects low pathogen count before symptoms e.g. HIV/cancer mutations), ELISA (detects antigen/antibody interaction), Recombinant probes autoradiography",
          "Transgenic Animals: Animals with foreign genes engineered ($95\%$ are transgenic mice); Uses: studying normal physiology/development, disease models (cancer, cystic fibrosis, Alzheimer's), biological products ($\alpha$-1-antitrypsin for emphysema, transgenic cow Rosie produced human protein-enriched milk $2.4\text{ g/L}$ containing $\alpha$-lactalbumin), vaccine safety testing (polio vaccine in mice), chemical toxicity testing",
          "Ethical Issues: GEAC (Genetic Engineering Appraisal Committee in India approves GM research and safety); Biopiracy (unauthorized use of bio-resources without compensation, e.g. patenting Indian Basmati rice cross by RiceTec, Neem, Turmeric)"
        ],
        commonMistakeWarning: "Pro-insulin me extra C-peptide present hota hai jo functional mature insulin me absent hota hai; Eli Lilly company ne A aur B chains alag-alag banakar disulfide bonds se connect kiye the."
      },
      {
        id: "bio-12-11",
        chapter: "Organisms and Populations (Ecology 1)",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Class 10 Ecology Basics"],
        coreSkills: [
          "Organism and its environment: Abiotic factors (Temperature: Eurythermal wide range vs Stenothermal narrow range; Water: Euryhaline vs Stenohaline; Light; Soil); Responses to abiotic factors (Regulators maintain homeostasis e.g. mammals, Conformers $99\%$ animals internal changes with environment, Partial regulators, Migrate e.g. birds to Keoladeo Ghana National Park, Suspend: hibernation in bears, aestivation in snails/fishes, diapause suspended development in zooplankton)",
          "Adaptations: Kangaroo rat in North American desert (internal fat oxidation water, concentrated urine), Desert plants (thick cuticle, sunken stomata, CAM pathway, Opuntia leaves modified to spines, stems phylloclade), Allen's Rule (mammals in colder climates have shorter ears and limbs to minimize heat loss), Blubber thick fat layer in seals, High altitude sickness physiological adaptation (body compensates low $pO_2$ by increasing RBC production, increasing breathing rate, decreasing hemoglobin binding affinity)",
          "Populations: Population attributes (Birth rate, Death rate, Sex ratio, Age pyramids: Expanding triangular, Stable bell-shaped, Declining urn-shaped); Population Density ($N$); Population Growth models: Exponential Growth ($\frac{dN}{dt} = rN$, $N_t = N_0 e^{rt}$, J-shaped curve when resources unlimited) vs Logistic Growth / Verhulst-Pearl Logistic ($\frac{dN}{dt} = rN(\frac{K - N}{K})$, Sigmoid S-shaped curve where $K$ is Carrying Capacity)",
          "Population Interactions: Mutualism ($+/+$, e.g. Lichens algae/fungi, Mycorrhiza, Fig tree and Wasp partner, Ophrys orchid sexual deceit of pseudo-copulation with male bee), Commensalism ($+/0$, e.g. Orchid on mango branch, Barnacles on whale, Cattle egret and grazing cattle, Sea anemone and Clown fish), Predation ($+/-$, biological control, maintains species diversity e.g. Pisaster starfish removal caused extinction of 10 invertebrate species), Parasitism ($+/-$, Ectoparasites lice/ticks vs Endoparasites liver fluke; Brood parasitism: Cuckoo lays eggs in Crow's nest), Competition ($-/-$, Gause's Competitive Exclusion Principle: two species competing for same limiting resource cannot coexist permanently; Resource Partitioning by MacArthur: 5 warbler species coexisting on same tree by different foraging times), Amensalism ($-/0$, e.g. Penicillium fungus secretes penicillin inhibiting bacteria)"
        ],
        commonMistakeWarning: "Gause's Competitive Exclusion principle tabhi apply hota hai jab resources strictly limiting hon; agar resources abundant hon ya species resource partitioning show karein to coexistence possible hai."
      },
      {
        id: "bio-12-12",
        chapter: "Ecosystem (Ecology 2)",
        classLevel: "12th",
        difficultyRating: "Medium",
        mandatoryChapters: ["Organisms and Populations"],
        coreSkills: [
          "Ecosystem structure and function: Productivity (Gross Primary Productivity / GPP total organic matter synthesized, Net Primary Productivity / NPP $= GPP - R$ available to consumers; Secondary Productivity rate of biomass formation by consumers; Annual NPP of whole biosphere $\approx 170\text{ billion tons}$, oceans only $55\text{ billion tons}$ despite covering $70\%$ surface)",
          "Decomposition steps: Fragmentation (by detritivores like earthworm), Leaching (water-soluble inorganic nutrients seep into soil horizon), Catabolism (fungal and bacterial enzymes degrade detritus), Humification (leads to dark amorphous substance Humus highly resistant to microbes), Mineralization (release of inorganic nutrients); Decomposition is slow if detritus is rich in lignin and chitin, fast if rich in nitrogen and sugars",
          "Energy Flow: $10\%$ Law of energy transfer by Lindeman (only $10\%$ energy transferred to next trophic level, rest lost as heat); Food Chains: Grazing Food Chain (GFC, begins with producers) vs Detritus Food Chain (DFC, begins with dead organic matter, major conduit of energy in terrestrial ecosystems)",
          "Ecological Pyramids: Pyramid of Numbers (mostly upright, inverted in single tree supporting birds and parasites), Pyramid of Biomass (upright in terrestrial, inverted in aquatic sea ecosystem where biomass of fishes exceeds phytoplankton), Pyramid of Energy (always upright, never inverted, follows 2nd law of thermodynamics)",
          "Ecological Succession: Primary succession (on bare rock / newly cooled lava, very slow) vs Secondary succession (abandoned farmland, burned forest, faster); Hydrarch (Pioneer stage Phytoplankton $\to$ Submerged $\to$ Floating $\to$ Reed swamp $\to$ Marsh meadow $\to$ Scrub $\to$ Climax Forest) vs Xerarch (Pioneer stage Lichens $\to$ Mosses $\to$ Annual herbs $\to$ Perennial grasses $\to$ Shrubs $\to$ Climax Mesic Forest)"
        ],
        commonMistakeWarning: "Pyramid of Energy hamesha upright hota hai aur kabhi bhi inverted nahi ho sakta kyunki energy ek trophic level se doosre level tak flow hone par heat ke roop me dissipate hoti hai ($10\%$ rule)."
      },
      {
        id: "bio-12-13",
        chapter: "Biodiversity and Its Conservation (Ecology 3)",
        classLevel: "12th",
        difficultyRating: "Low",
        mandatoryChapters: ["Ecosystem"],
        coreSkills: [
          "Biodiversity levels: Genetic diversity (e.g. Rauwolfia vomitoria produces reserpine, $>50,000$ strains of rice, $1,000$ varieties of mango in India), Species diversity (Amphibians have higher species diversity in Western Ghats than Eastern Ghats), Ecological diversity (Deserts, Rain forests, Coral reefs, Mangroves)",
          "Global Species estimate by Robert May: Global biodiversity $\approx 7\text{ million}$ species; Insects comprise $>70\%$ of all animal species; Fungi species exceed total combined species of fishes, amphibians, reptiles, and mammals; India has $2.4\%$ of world's land area but accounts for $8.1\%$ of global species diversity (1 of the 12 mega diversity countries)",
          "Patterns of Biodiversity: Latitudinal gradient (Species diversity decreases from Equator towards Poles; Tropical Amazonian rain forest in South America has greatest biodiversity on earth); Species-Area Relationship by Alexander von Humboldt: $\log S = \log C + Z \log A$ where $S$ is species richness, $A$ is area, $Z$ is slope of regression line ($Z = 0.1-0.2$ for small regions, $Z = 0.6-1.2$ for entire continents / frugivorous birds)",
          "Loss of Biodiversity - 'The Evil Quartet': (1) Habitat Loss and Fragmentation (most important cause e.g. Amazon rain forest 'lungs of planet' cut for soybeans and cattle pasture), (2) Over-exploitation (Steller's sea cow, Passenger pigeon became extinct), (3) Alien / Invasive Species Invasions (Nile perch in Lake Victoria killed cichlid fishes, Parthenium / Carrot grass, Lantana, Water Hyacinth / Eichhornia 'Terror of Bengal', African catfish Clarias gariepinus threatening indigenous catfishes), (4) Co-extinctions (extinction of host fish causes extinction of unique parasites, plant-pollinator mutualism)",
          "Biodiversity Conservation: In-situ / On-site conservation (protecting endangered species in natural habitat: 34 Global Biodiversity Hotspots e.g. Western Ghats-Sri Lanka, Indo-Burma, Himalaya; 14 Biosphere Reserves, 90 National Parks, 448 Wildlife Sanctuaries, Sacred Groves e.g. Khasi & Jaintia hills in Meghalaya, Aravalli hills in Rajasthan) vs Ex-situ / Off-site conservation (taking threatened species out of natural habitat: Zoological parks, Botanical gardens, Wildlife safari parks, Cryopreservation of gametes at $-196^\circ\text{C}$ in liquid nitrogen, In vitro fertilization, Seed banks); Earth Summit in Rio de Janeiro (1992) and World Summit in Johannesburg (2002)"
        ],
        commonMistakeWarning: "Sacred Groves In-situ conservation ka example hain, jabki Botanical Gardens, Zoological Parks aur Seed Banks Ex-situ conservation ke examples hain."
      }
    ]
  }
};
